export default {
  common: { loading: 'Loading…', noData: 'No data', clear: 'Clear', delete: 'Delete' },
  errors: { /* notices-i18n */
    generic: 'Something went wrong. Please try again.',    codes: {
      // The clock card's refusals (time-api /timer/start and /timer/stop): the card
      // shows the specific reason, as time-ui's own clock button does (issue 700).
      leave_day: 'This day carries a full day of approved absence — no working time can be recorded on it.',
      // A person her contract frees from booking working time (issue 981): time-api refuses the clock-in with this code.
      work_time_booking_off: 'Your contract means you book project time only, not working time.',
      week_approved: 'This week is already approved and locked – changes only via a correction request.',
      week_submitted: 'This week is submitted and awaiting a decision – changes only once it is withdrawn or sent back.',
      already_clocked_in: 'You are already clocked in. Close the running working time first.',
      block_overlap: 'This working time overlaps another one.',
      block_locked: 'This working time is locked by an approved week. Request a correction instead.',
      // user-api's SSO connection PUT (#882): issuer and client are frozen while SSO is enforced.
      sso_enforced: 'Turn off “Require sign-in via SSO only” before changing the issuer URL or the client ID.',
      // The SSO connection PUT of user-api (issue 926): a list entry that is not a domain.
      invalid_domain: 'One of the email domains is not a domain. Enter domains like example.com, without wildcards or web addresses.',
      // ats-api reports this flat on the envelope; `errorKeyFor` prefers a
      // curated code entry over the status fallback, which is how the custom-field
      // dialog keeps its one specific message without reading the backend string.
      CUSTOM_FIELD_SENSITIVITY_LOCKED: 'This field is declared as special-category data and the declaration cannot be withdrawn.',
      LEGAL_NOTICE_BLANK_EMPTY: 'A blank cannot be filled with nothing.',
      LEGAL_NOTICE_BLANK_UNKNOWN: 'This notice has no blank by that name.',
      LEGAL_NOTICE_BLANK_TOKEN: 'A value cannot itself contain a blank.',
      // The employee-notice gate (issue 576 M3): the invite refusal names WHO
      // can clear it, so an admin sent here knows the fix is not theirs to retry.
      EMPLOYEE_NOTICE_INCOMPLETE: 'Invitations are paused until every blank in the employee notices under Legal notices is filled in. Ask the workspace owner, a suite admin, or the data protection officer to complete them.',
      LEGAL_READINESS_UNAVAILABLE: 'We could not check whether the employee notices are complete. Please try again in a few minutes.',
    } as Record<string, string>,
  },
  billing: {
    title: 'Billing', status: 'Status', paymentMethod: 'Payment method',
    updateCard: 'Replace card', addCard: 'Add card',
    invoices: 'Invoices', charges: 'Payments',
    noInvoices: 'No invoices yet.', noCharges: 'No payments yet.',
    noCard: 'None on file', validUntil: 'valid until {date}',
    trialDaysLeft: '{days} days left in trial', trialExpired: 'Trial expired',
    trialDaysLeftUntil: '{days} days left in trial — ends on {date}',
    // A free_months promo account (issue 808): the whole cardless run, never "trial end" at d2.
    trialDaysLeftFree: 'Free for {days} more days — until {date}',
    nextChargeOn: 'Next charge on {date}',
    exempt: { badge: 'Billing exempt', note: 'lumi has arranged for this workspace not to be billed right now.' },
    download: 'Download', dangerZone: 'Danger zone',
    col: { date: 'Date', period: 'Period', net: 'Net', vat: 'VAT', gross: 'Gross', status: 'Status', kind: 'Type', amount: 'Amount', document: 'Invoice' },
    statusLabel: { trialing: 'Trial', active: 'Active', delinquent: 'Payment due', closed: 'Closed', canceled: 'Canceled' },
    tab: { plans: 'Plans', paymentMethod: 'Payment method', invoices: 'Invoices & payments', referral: 'Referral', externalPostings: 'Job adverts', dangerZone: 'Cancel & delete' },
    externalPostings: {
      title: 'External job adverts',
      intro: 'Adverts on external job boards are ordered one at a time and charged per order — up to several thousand euros each. With this on, anyone whose recruiting role holds the “Post externally (paid)” permission can place those orders — at first only the recruiting admins; every order is billed to this workspace.',
      switchLabel: 'Allow ordering adverts on external job boards',
      saving: 'Saving',
      offHint: 'Switching this off only stops new orders. Adverts that are already live stay online and are still billed until their term ends.',
      offHintOpenOrders: 'Adverts are live right now. Switching this off only stops new orders — the running adverts stay online and are still billed until their term ends.',
      roleNote: 'Only a workspace owner, a billing manager or a suite admin can change this setting. Someone who should not order adverts is stopped in recruiting instead, by withdrawing the “Post externally (paid)” permission from their role.',
      demoNote: 'This is a demo workspace. Its settings are replaced when you complete the real setup, so this switch stays locked until then.',
      noCardTitle: 'No payment method on file',
      noCardBody: 'You can turn this on now, but an advert order cannot be paid until a card is on file.',
      noCardCta: 'Add a card',
      savedOn: 'External job adverts are on. Recruiters will see the ordering section on their next page load.',
      savedOff: 'External job adverts are off. No new adverts can be ordered; adverts that are already live stay online and are still billed.',
    },
    invoiceStatus: { draft: 'Being prepared', issued: 'Issued', sent: 'Sent', canceled: 'Canceled' },
    chargeKind: { subscription: 'Subscription', proration: 'Proration', posting: 'Job posting', recovery: 'Recovery payment' },
    posting: { orderRef: 'Order {ref}', invoice: 'Download invoice' },
    chargeStatus: { created: 'Created', pending: 'Pending', paid: 'Paid', failed: 'Failed', authorized: 'Reserved', order_placed: 'Placed', capture_requested: 'Capture requested', captured: 'Paid', settled: 'Paid', refunded: 'Refunded', chargeback: 'Chargeback', voided: 'Failed' },
    bill: { label: 'Invoice {number}', labelMany: 'Invoices {number}', unnumbered: 'Invoice being prepared', paid: 'Paid', closed: 'Closed', retry: 'Retry', projected: 'Projected' },
    // The promo (issue 808, ADR 0100): one line for the phase the account is in.
    promo: { trial: 'Trial until {start} · then 1 free month until {end} · code {code} | Trial until {start} · then {n} free months until {end} · code {code}', run: 'Free months until {end} · code {code}', discount: 'Trial until {start} · then 1 month at {percent}% off seats and modules · code {code} | Trial until {start} · then {n} months at {percent}% off seats and modules · code {code}', discountRun: '{percent}% off seats and modules until {end} · code {code}', invoiceLine: 'Promo code {code} −{percent}%', referralLine: 'Referral bonus −{percent}%', referralLineCapped: 'Referral bonus −{percent}% (capped)' },
    // A module code typed in the add-module dialog (issue 1029, M2).
    modulePromo: { label: 'Promo code (optional)', apply: 'Apply', remove: 'Remove', applied: '{code}: {percent} % off {module} for 1 bill | {code}: {percent} % off {module} for {n} bills', savesToday: 'The code takes {amount} off today\'s charge.', rateLimited: 'Too many tries. Please wait a few minutes and try again.', checkFailed: 'The code could not be checked right now. You can add the module without it.', notBooked: 'Nothing was booked – you can add the module without the code.', addedWithoutDiscount: 'The code could not be applied, so there is no discount.', module: { time_tracking: 'Time tracking' }, trimmed: '{code} gives {percent} %, but your bill already carries a discount – on {module} you get {effective} % (all discounts on one bill are capped).', trimmedNothing: '{code} gives {percent} %, but your bill already carries a discount – this code adds nothing on top right now.', pending: 'We\'re still confirming your code – it will show on your next bill once confirmed.', invoiceLine: 'Promo code {code} −{percent}% ({module})', running: '{code}: {percent} % off for 1 more bill | {code}: {percent} % off for {n} more bills', reason: { unknown: 'We don\'t know this code. Please check how it is spelled.', disabled: 'This code has been switched off.', notYetValid: 'This code is not valid yet.', expired: 'This code has expired.', exhausted: 'You\'ve already used this code, or it has been used up.', moduleNotEligible: 'This code doesn\'t cover {module}.', wrongCheckout: 'This code is for new sign-ups, not for adding a module.', unavailable: 'This code can\'t be used here.' } },
    // The billing door (issue 824): a promo code entered on a running workspace.
    // The workspace's own referral code (issue 824), shown to those who manage billing.
    referral: { title: 'Your referral code', copy: 'Copy code', copyLink: 'Copy checkout link', copied: 'Referral code copied', linkCopied: 'Checkout link copied', friendGets: 'A friend who uses your code gets {percent} % off their first paid month. | A friend who uses your code gets {percent} % off their first {n} paid months.', youGet: 'For every company that starts paying with your code: {percent} % off one of your bills — several add up, up to {ceiling} % per bill. | For every company that starts paying with your code: {percent} % off {n} of your bills — several add up, up to {ceiling} % per bill.', youGetCapped: 'For every company that starts paying with your code: {percent} % off one of your bills — several add up, up to {ceiling} % per bill; each bonus takes off at most {cap} per invoice. | For every company that starts paying with your code: {percent} % off {n} of your bills — several add up, up to {ceiling} % per bill; each bonus takes off at most {cap} per invoice.', paused: 'Your code is paused while a deletion request is open for your workspace. It works again as soon as the request is withdrawn.', disabled: 'This code was switched off and can no longer be used.', none: { trialing: 'A referral code comes once your workspace pays, after the trial.', free_run: 'A referral code comes once the free months are over and your workspace pays.', not_billing: 'A referral code comes once your workspace has a running subscription.' }, loading: 'Reading your referral code …', error: 'Your referral code could not be read right now. Please try again later.', bonus: { title: 'Your referral bonuses', collect: 'Collect', ready: 'ready to collect', startsOn: 'starting with your bill of {date}', startsNext: 'starting with your next bill', waits: 'waiting for room on a coming bill', carries: 'Your next bill carries {items}.', capped: 'capped', running: { promo: '{percent} % campaign code', module: '{percent} % module code', referral: '{percent} % referral bonus' }, collected: 'Collected — the bonus now comes off your next bills.', alreadyRunning: 'This bonus is already running.', collectFailed: 'The bonus could not be collected right now. Please try again.', hint: { ceiling: 'Not yet: your next bill already carries {running}, and with this bonus it would pass the {ceiling} % limit.', ceilingRunning: 'Not yet: your next bill already carries {running}, which leaves no room for this bonus.', ceilingLimit: 'Not yet: with this bonus your next bill would pass the {ceiling} % limit.', ceilingPlain: 'Not yet: the discounts on your next bill leave no room for this bonus.', roomNext: 'A running bonus ends with your next bill.', roomLater: 'The soonest a running bonus ends is after {n} more bills.', notActive: 'This bonus waits until your workspace is active again.', deletion: 'This bonus waits while a deletion request is open for your workspace.', unavailable: 'Whether this bonus can be collected cannot be checked right now. Please try again shortly.' } }, history: { title: 'Your referrals', empty: 'No one has used your code yet.', waiting: 'Used on {date} — waiting for their first paid month', bonusEarned: 'Earned {date}: {percent} % off 1 bill | Earned {date}: {percent} % off {n} bills', bonusCap: ', capped at {cap} per invoice', bonusOn: 'on your {months} bill | on your {months} bills', bonusToCome: '{n} to come', bonusNone: 'not on a bill yet' } },
    errors: { setupFailed: 'Card setup failed.', loadFailed: 'Could not load billing data.', ownerOnly: 'Only the workspace owner can manage billing.', planChangeFailed: 'Could not change your plan. Please try again.', paymentStanding: 'Resolve your outstanding payment before upgrading.', cardRequired: 'Add a valid card before upgrading.', addModuleFailed: 'Could not add the module. Please try again.', cancellationPending: 'Your subscription is scheduled to end. Choose “Keep subscription” first, then add the module.', removeFailed: 'Could not schedule the removal. Please try again.' },
    retry: 'Try again',
    common: { close: 'Close' },
    card: {
      label: 'Card', title: 'Payment method',
      remove: 'Remove card', removed: 'Card removed.',
      inUse: 'This card has already been billed and can only be replaced, not removed.',
      removeConfirmTitle: 'Remove card?', removeConfirmBody: 'This removes the card on file. You can add a new one anytime.',
      removeError: 'Could not remove the card.',
      handoff: 'Send a payment link to someone else', handoffTitle: 'Payment link',
      handoffHint: '"Add/replace card": you enter the card details yourself. "Send a payment link to someone else": send a single-use link to whoever sets up the card — e.g. your finance team.',
      handoffBody: 'Share this single-use link with whoever sets up the card. It expires {date}.',
      copy: 'Copy', copied: 'Copied', handoffCopied: 'Payment link copied', handoffError: 'Could not create a payment link.',
    },
    recovery: {
      title: 'Payment due · {amount}',
      body: 'Your bank needs to confirm this payment. Confirm it now to keep your access.',
      cta: 'Confirm payment',
      paid: 'Payment received – your access is restored.',
      failed: 'The payment was not completed. You can try again at any time.',
      checking: 'We are still checking the payment…',
      startError: 'The payment could not be started right now. Please try again in a moment.',
    },
    plan: {
      title: 'Plan & modules', subtitle: 'Manage the plan for each active module.',
      currentPlan: 'Current plan', current: 'Current',
      perMonth: '{amount}/mo', perUserMonth: '{amount} per user/mo.', perApprenticeMonth: '{amount} per apprentice/mo.', volumeHint: 'Volume discount: larger workspaces pay less per seat, applied automatically.',
      perUpgradeMonth: '{amount} per upgrade/mo.',
      upgrade: 'Upgrade', downgrade: 'Downgrade', apply: 'Change plan',
      downgradeNote: 'Downgrades take effect at the start of your next billing cycle.',
      fixCardCta: 'Fix payment method',
      enterprise: { title: 'Need more?', body: 'Larger teams and custom terms are arranged with our team.', cta: 'Contact sales' },
      provisioning: 'Setting up — this can take a minute.',
      provisioningFailed: 'Setup failed — we are on it.',
      removeModule: 'Remove module', removeConfirmTitle: 'Remove module?',
      removeConfirmBody: '{module} will be removed at the end of your current billing cycle. You can undo this until then.',
      removeScheduled: 'Removal of {module} scheduled.',
      addTitle: 'Add a module', addSubtitle: 'Extend your workspace with another module.',
      addCta: 'Add module', addConfirmTitle: 'Add module?',
      moduleLabel: 'Module', planLabel: 'Plan',
      // The booking table's column heads and its sum row's start date (#409 F12).
      booking: { price: 'Price', seats: 'Seats', monthly: 'Monthly', from: 'from {date}' },
      apprenticeSeats: 'Booked apprentice seats',
      apprenticeSeatsHint: 'At least 1 apprentice seat for the apprenticeship module.',
      apprenticeSeatsCeiling: 'Above {max} apprentice seats there is no self-serve plan – we agree it with you directly:',
      apprenticeSeatAdjustment: 'You booked {booked} apprentice seats before this module was removed, so it has been re-added with {booked} instead of the {requested} you asked for. You can reduce them from your next billing date.',
      added: '{module} added.',
      addedPaymentRetrying: '{module} was added and is ready to use. We could not take the payment just now — we will retry it automatically.',
      alreadyActive: '{module} is already on your plan.',
    },
    seats: {
      title: 'Booked seats',
      userSeats: 'User seats', apprenticeSeats: 'Apprentice seats',
      stepUp: 'More seats', stepDown: 'Fewer seats',
      bookMore: 'Book more seats',
      confirmTitle: 'Book more seats?', confirmCta: 'Book seats',
      chargeNote: 'Booking more seats charges a prorated amount for the rest of this billing cycle.',
      confirmTitleDecrease: 'Reduce booked seats?',
      reduceCta: 'Reduce seats',
      decreaseNote: 'Nothing is charged or credited today — your booking changes on {date}.',
      decreaseNoteImmediate: 'Nothing is charged or credited — your booking changes right away.',
      decreaseScheduled: 'Seat reduction scheduled for {date}.',
      mixedDirection: 'Book more seats and reduce seats in separate steps: added seats are charged today, reduced seats take effect at your next billing date.',
      belowUsage: 'Your workspace is using more seats than that. Keep at least {count}.',
      usageCheckUnavailable: 'We could not check how many seats are in use right now. Please try again in a moment.',
      leaveFreelancer: 'This leaves freelancer mode.',
      returnFreelancer: 'This returns you to freelancer mode — one person, one seat.',
      booked: 'Your booked seats have been updated.',
      bookedPaymentRetrying: 'Your seats are booked and ready to use. We could not take the payment just now — we will retry it automatically.',
      bookFailed: 'Could not book the seats. Please try again.',
    },
    seatPanel: {
      inUse: '{used} of {booked} in use',
      bookedOnly: '{booked} booked',
      useUnknown: 'We could not read how many are in use right now.',
      manage: 'Manage seats',
      pendingUsers: 'User seats change to {target} on {date} — you are booked for {current} until then.',
      pendingApprentices: 'Apprentice seats change to {target} on {date} — you are booked for {current} until then.',
      reflooredUsers: 'Your reduction to {requested} user seats applied at {booked}: {used} people are in this workspace and each one holds a seat. Remove the people you no longer need, then reduce again.',
      reflooredApprentices: 'Your reduction to {requested} apprentice seats applied at {booked}: {used} apprenticeships are running. Finish or end the ones you no longer need, then reduce again.',
      // A demo workspace's apprentices hold no booked seat (issue 364 F4/F30):
      // the creation gate ignores the booking while the demo runs, so the count
      // excludes them and this row says so.
      demoNotCounted: 'Demo apprenticeships do not use booked seats — they are not counted here and disappear when you commit the demo.',
    },
    // The seat price calculator (issue 364 F30). Every FIGURE it shows arrives
    // from the API; these are only the words around them.
    calc: {
      title: 'What this booking costs',
      includes: 'What your booking includes, and what it costs per month.',
      userSeatsCount: '{count} user seats',
      apprenticeSeatsCount: '{count} apprentice seats',
      aiQuotaUpgradesCount: '{count} × upgrade',
      subtotal: 'Subtotal',
      volumeDiscount: 'Volume discount',
      monthlyTotal: 'Monthly total',
      currentTotal: 'Today you pay {amount} per month.',
      newMonthlyTotal: 'From your next invoice: {amount} per month.',
      netNote: 'Prices exclude VAT. Booking more seats also charges a one-off prorated amount for the rest of the current billing cycle.',
    },
    // The AI quota card (#665). Every figure it shows arrives from the API.
    aiQuota: {
      title: 'AI quota',
      usageLink: 'Where it is spent',
      ownKey: 'This workspace runs lumi on its own AI provider key, so it has no AI quota — your provider bills you for every token directly.',
      suspended: 'The AI quota is paused while this workspace is suspended.',
      unlimited: 'No AI quota applies to this workspace — lumi has lifted the limit.',
      intro: 'lumi, the AI features in every module and the external AI agents members connect share one quota: up to {limit} tokens per billing period. It resets at the start of every billing period; unused tokens do not carry over.',
      included: 'Included',
      includedDerived: 'with your booked seats',
      includedOverride: 'set for this workspace by lumi',
      tokensValue: '{tokens} tokens per period',
      upgrades: 'Quota upgrades',
      upgradesNone: 'None booked',
      upgradesValue: '{count} booked · +{tokens} tokens',
      used: 'Used this period',
      usedValue: '{used} of {limit} tokens',
      usedNoLimit: '{used} tokens so far',
      resetsOn: 'Resets on {date}',
      stepPrice: 'One upgrade adds {tokens} tokens per billing period for {amount} per month.',
      stepUp: 'One more upgrade',
      stepDown: 'One upgrade fewer',
      removeCta: 'Remove at the next billing date',
      confirmTitle: 'Add an AI quota upgrade?',
      confirmTitleDecrease: 'Remove an AI quota upgrade?',
      confirmCta: 'Book upgrade',
      trialWarning: 'This costs real money, even during your trial: your card is charged today, prorated for the rest of the trial but for one month at most, and every month after it ends.',
      cardRequired: 'An AI quota upgrade needs an active card, even during the trial. Add your card first — once it is confirmed, you can book the upgrade.',
      promoExcluded: 'Your code does not apply to the AI quota: an upgrade is never discounted and is charged at the list price.',
      chargeNote: 'An upgrade charges a prorated amount for the rest of this billing period and applies right away.',
      decreaseNote: 'Nothing is charged or credited today. You keep the upgrade until {date}; from then on the smaller quota applies.',
      decreaseNoteGeneric: 'Nothing is charged or credited today — the upgrade ends at your next billing date.',
      newQuota: 'Afterwards: up to {limit} tokens per billing period.',
      changeDelta: '{tokens} tokens · {amount} / month',
      addCtaPrice: 'Add quota upgrade · {amount} / month',
      booked: 'Your AI quota upgrades have been updated.',
      bookedPaymentRetrying: 'Your AI quota upgrade is booked and applies right away. We could not take the payment just now — we will retry it automatically.',
      decreaseScheduled: 'The AI quota upgrade ends on {date}.',
      pending: 'Quota upgrades change to {target} on {date} — you are booked for {current} until then.',
      bookFailed: 'Could not change the AI quota upgrades. Please try again.',
    },
    scheduled: {
      resizeTo: 'Switch to {plan}', remove: 'Remove module',
      effective: 'effective {date}', revert: 'Undo', reverted: 'Scheduled change reverted.',
    },
    proration: {
      title: 'Confirm plan change', stepFrom: 'Current', stepTo: 'New',
      chargeNote: 'Upgrading now charges a prorated amount for the rest of this billing cycle.',
      freeNote: 'This change applies immediately at no extra cost during your trial.',
      confirm: 'Confirm & upgrade', cancel: 'Cancel',
      charged: 'Charged {amount}.', appliedImmediate: 'Your new plan is active.',
      appliedScheduled: 'Scheduled — effective {date}.',
      calculating: 'Working out the exact amount…',
      chargeExact: 'You will be charged {amount} today.',
      noChargeToday: 'No charge today.',
    },
    profile: {
      title: 'Billing details', subtitle: 'Complete your invoicing details to activate payment.',
      legalName: 'Legal company name', addressLine1: 'Address', addressLine2: 'Address line 2',
      postalCode: 'Postal code', city: 'City', vatId: 'VAT-ID',
      vatOptional: 'Optional — required for reverse-charge within the EU.',
      save: 'Save details', edit: 'Edit billing details', error: 'Could not save your billing details.',
      saved: 'Billing details saved.',
      vatPending: 'We could not verify your VAT-ID just now. Please try again shortly.',
      vatInvalid: 'This VAT-ID could not be validated. Please check it.',
      vatRequired: 'A valid VAT-ID is required for your country before adding a card.',
      completeFirst: 'Complete your billing details before adding a card.',
    },
    cancel: {
      title: 'Cancel subscription',
      body: 'Cancel your subscription. Your workspace stays active until the end of the current billing cycle.',
      reason: 'Reason (optional)', confirm: 'Cancel subscription', confirmTitle: 'Cancel subscription?',
      confirmBody: 'Your subscription ends {date}. Everything keeps working until then.',
      note: 'Until then everything keeps working, including the export of the whole workspace. After that the workspace is closed; its data stays for a while, and the owners are told before anything is deleted. If it should go sooner, use “{deletion}”.',
      pending: 'Cancellation scheduled — your subscription ends {date}.', pendingNoDate: 'Cancellation scheduled.',
      revert: 'Keep subscription', error: 'Could not cancel your subscription.',
      done: 'Cancellation scheduled. Your workspace stays active until the end of the current billing cycle.', revertDone: 'Cancellation reverted. Your subscription continues as normal.',
    },
    deletion: {
      title: 'Delete workspace (GDPR)',
      body: 'Request permanent deletion of this workspace and all its data. After a short grace period this cannot be undone.',
      request: 'Request deletion', reason: 'Reason (optional)',
      confirmTitle: 'Request workspace deletion?',
      confirmBody: 'All data will be permanently erased after a short grace period. This cannot be undone.',
      pending: 'Deletion requested — data is erased after {date}.', pendingNoDate: 'Deletion requested and pending review.',
      cancel: 'Cancel deletion request', error: 'Could not submit your deletion request.',
      requested: 'Deletion requested. We’ll erase your workspace after the grace period.', canceled: 'Deletion request withdrawn. Your workspace stays.',
      confirmType: 'Type the workspace name ({name}) to confirm',
      cancelConfirmTitle: 'Withdraw the deletion request?',
      cancelConfirmBody: 'Your workspace and all its data are kept. You can request deletion again at any time.',
    },
    moduleNames: { core: 'lumi-HR Core', ats: 'Recruiting', time_tracking: 'Time', apprenticeship: 'Ausbildung' },
    planNames: {
      ats: { seat: 'Per user', enterprise: 'Enterprise', unknown: '—' },
      time: { seat: 'Per user', enterprise: 'Enterprise', unknown: '—' },
      apprenticeship: { seat: 'Per apprentice', enterprise: 'Enterprise', unknown: '—' },
      // People Pro is retired (#596), but issued invoices keep their `peoplePro.seat`
      // line (GoBD) and render through this entry — keep it while any exists.
      peoplePro: { seat: 'Per user', enterprise: 'Enterprise', unknown: '—' },
      // The AI quota upgrade line (#665): its displayKey is `ai_quota.upgrade`.
      ai_quota: { upgrade: 'AI quota upgrade (+1,000,000 tokens per billing period)' },
    },
    onboard: {
      // Three honest branches, never a CTA without a control (#364 F18/F23).
      // `exempt` is checked FIRST, ahead of demo: an operator-granted exemption
      // is a deliberate state, not a broken signup and not a demo limitation.
      exempt: { title: 'This workspace is exempt from billing', body: 'lumi has arranged for this workspace not to be billed, so it has no billing account and nothing is or will be charged. Only lumi can change that — there is nothing to set up here.' },
      demo: { title: 'Billing is not available in demo mode', body: 'This workspace is running on demo data, so there is nothing to bill for yet. Billing can be set up once you commit the demo to a real setup — the demo banner at the top of the page starts that.' },
      // The stranded signup's real recovery is the buyer redoing the SAME checkout,
      // which resumes THIS workspace (issue 364 F25) — support is the fallback, and
      // for anyone who is not the buyer it is the only route they have.
      stranded: { title: 'Billing setup did not finish', buyerBody: 'Your signup did not complete, so this workspace has no billing account yet and nothing is being charged. You can finish it yourself: go back to checkout and buy the same modules again — that picks this workspace up where it stopped instead of creating a second one. If it still does not go through, get in touch and we will finish it with you.', otherBody: 'Your signup did not complete, so this workspace has no billing account yet and nothing is being charged. Only the person who signed this workspace up can finish it, by going back to checkout and buying the same modules again. Ask them to do that, or get in touch and we will finish it with you.', checkoutCta: 'Back to checkout', cta: 'Contact support' },
    },
    // The four documents this workspace's billing relationship is
    // governed by, on the landing host (#576 M2) — not a "version you
    // accepted" claim, just the current published text.
    contractDocuments: {
      title: 'Contract documents',
      terms: 'Terms & Conditions',
      avv: 'Data Processing Agreement (AVV)',
      tom: 'Technical & Organisational Measures (TOM)',
      subprocessors: 'Subprocessors',
    },
  },
  login: {
    ssoApple: 'Sign in with Apple',
    passkeySignIn: 'Sign in with a passkey',
    passkeySigningIn: 'Signing in…',
    passkeyFailed: 'Passkey sign-in failed. Try again, or use your password.',
    title: 'lumi-HR Suite',
    subtitle: 'Sign in to continue',
    email: 'Email',
    emailPlaceholder: "name{'@'}company.com",
    password: 'Password',
    passwordPlaceholder: 'Your password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    failed: 'Login failed',
    forgotPassword: 'Forgot password?',
    lostPhone: 'Lost your phone?',
    orContinueWith: 'or continue with',
    ssoGoogle: 'Sign in with Google',
    ssoMicrosoft: 'Sign in with Microsoft',
    ssoEnterprise: 'Sign in with company SSO',
    ssoOnly: 'Sign-in for this workspace goes through your company SSO.',
    adminLogin: 'Administrator sign-in',
    ssoError: 'SSO sign-in failed. Please try again.',
    ssoSeatLimit: 'This workspace has no free user seat, so we could not create an account for you. Ask the workspace owner or a Billing Manager to book another seat — signing in again will not help until they do.',
    ssoLinkRequired: 'This company sign-in could not be connected to your lumi account automatically. Sign in another way, then connect company sign-in under Account & security.',
    ssoEmployeeNoticeIncomplete: 'This workspace is not ready yet: the employee notices still have open blanks. Ask the workspace owner, a suite admin or the data protection officer to fill them in the Legal notices section — signing in again will not help until they do.',
    ssoLegalReadinessUnavailable: 'We could not check whether this workspace is ready just now. Please try again in a few minutes.',
    ssoOperatorPasswordOnly: 'System admin accounts sign in with their password and authenticator app, not with this provider.',
    mfaTitle: 'Enter your authenticator code',
    mfaIntro: 'This is a system admin account. Enter the 6-digit code from your authenticator app, or one of your recovery codes.',
    mfaCode: 'Code',
    mfaSubmit: 'Sign in',
    mfaBack: 'Back',
    mfaInvalid: 'That code is not correct.',
    mfaReused: 'That code was already used. Wait for the next one in your app.',
    mfaExpired: 'This sign-in has expired. Sign in again with your password.',
    mfaEnrolOnConsole: 'This system admin account has no authenticator app yet. Set one up by signing in to the lumi console first.',
    tenantLogoAlt: 'Workspace logo',
    poweredBy: 'Powered by',
  },
  loginBranding: {
    nav: 'Login screen',
    title: 'Login screen',
    intro: 'Give this workspace’s sign-in page its own logo, colour and background image. Everyone who signs in here sees it.',
    retry: 'Try again',
    loadFailed: 'The current settings could not be loaded.',
    form: {
      title: 'Appearance',
      logo: 'Logo',
      logoHint: 'PNG, JPEG, WebP or GIF, up to 2 MB. Leave empty to keep the lumi logo.',
      image: 'Background image',
      imageHint: 'Shown behind the sign-in card. PNG, JPEG, WebP or GIF, up to 8 MB.',
      upload: 'Upload',
      replace: 'Replace',
      remove: 'Remove',
      empty: 'No image',
      uploaded: 'Image uploaded.',
      removed: 'Image removed.',
      tooLarge: 'That file is too large.',
      wrongType: 'Only PNG, JPEG, WebP and GIF images are accepted.',
      uploadFailed: 'The image could not be uploaded. Please try again.',
      removeFailed: 'The image could not be removed. Please try again.',
      removeConfirm: 'The image is removed from the sign-in page and deleted. This cannot be undone.',
      primaryColor: 'Primary colour',
      primaryColorHint: 'Colours the workspace name, the links and the sign-in button.',
      boxColor: 'Box colour',
      boxColorHint: 'The box the sign-in form sits in.',
      boxColorInvalid: 'Enter a hex colour such as #ffffff.',
      boxOpacity: 'Box opacity',
      boxTone: 'Text and fields',
      boxToneHint: 'Suggested from the box colour. Switch it if the text is hard to read.',
      toneLight: 'Light box, dark text',
      toneDark: 'Dark box, light text',
      nothingToSave: 'Nothing to save: nothing has changed since the last save.',
      fixFirst: 'Fix the highlighted field first.',
      colorPlaceholder: 'e.g. 1a2b3c',
      colorInvalid: 'Enter a hex colour such as 1a2b3c.',
      clear: 'Clear',
      save: 'Save',
      saved: 'Login screen updated.',
      discard: 'Discard',
      liveNote: 'Saved changes are live the next time the sign-in page is opened.',
    },
    preview: {
      title: 'Preview',
      hint: 'How the sign-in page looks with the values above.',
      logoAlt: 'Logo preview',
    },
  },
  authFlow: {
    forgot: {
      title: 'Reset your password',
      subtitle: 'We\'ll send you a link to reset it.',
      email: 'Email',
      submit: 'Send link',
      done: 'If an account with that email exists, we\'ve sent a reset link.',
      backToLogin: 'Back to sign in',
    },
    reset: {
      title: 'Set a new password',
      password: 'New password',
      confirm: 'Confirm password',
      submit: 'Save password',
      success: 'Your password has been changed. You can now sign in.',
      goToLogin: 'Go to sign in',
      invalid: 'This link is invalid or has expired.',
      mismatch: 'The passwords don\'t match.',
      tooShort: 'Password must be at least 8 characters.',
      missingToken: 'No valid token found.',
    },
    invite: {
      title: 'Set up your account',
      subtitle: 'Choose a password to activate your account.',
      forTenant: 'Invitation to {tenant}',
      password: 'Password',
      confirm: 'Confirm password',
      submit: 'Activate account',
      submitting: 'Activating…',
      invalid: 'This invitation is invalid or has expired.',
      mismatch: 'The passwords don\'t match.',
      tooShort: 'Password must be at least 8 characters.',
      passkey: {
        title: 'Set up your sign-in',
        intro: 'You sign in to lumi with a passkey: your fingerprint, face or device PIN. No password to remember.',
        sentTo: 'Sent to {phone}',
        create: 'Create a passkey',
        creating: 'Waiting for your device…',
        instead: 'Use a passkey instead',
        or: 'or',
        unsupported: 'This browser cannot create a passkey. Open the link on your phone or in an up-to-date browser.',
        unavailable: 'Passkeys are not available for this workspace here. Please contact your administrator.',
        expired: 'That took too long. Please try again.',
        alreadyRegistered: 'This device already has a passkey for lumi.',
        failed: 'The passkey could not be created. Please try again.',
      },
    },
    recover: {
      title: 'Lost your phone?',
      subtitle: 'Enter your mobile number and we’ll text you a link to set up a new passkey.',
      phone: 'Mobile number',
      phoneHint: 'With the country code, e.g. +44 7700 900123',
      submit: 'Send link',
      done: 'If this number belongs to an account here, we’ve sent it a link. It is valid for 15 minutes.',
      enrolTitle: 'Set up a new passkey',
      enrolSubtitle: 'Create a passkey on this device. Every other session of your account is signed out.',
      create: 'Create a new passkey',
      invalid: 'This link is invalid or has expired. Ask for a new one.',
    },
    verify: {
      verifying: 'Confirming your email…',
      success: 'Your email address has been confirmed.',
      invalid: 'This confirmation link is invalid or has expired.',
      backToLogin: 'Go to sign in',
    },
  },
  account: {
    // AI agent connections on the account page (ADR 0103 rule 13).
    agentConnections: {
      title: 'AI agent connections',
      hint: 'AI clients you have allowed to act as you in a workspace. Each one works with exactly your permissions, and what it reads goes to that client’s provider.',
      none: 'No AI agent is connected.',
      inWorkspace: 'in the workspace {workspace}',
      scope: {
        read: 'read',
        act: 'act with confirmation',
        ask: 'ask lumi',
      },
      lastUsed: 'Last used on {when}',
      connectedOn: 'Connected on {when}, not used yet',
      standing: {
        label: 'Go ahead with actions on my own records without asking',
        hint: 'Never covers other people’s records or adverse decisions.',
        notOffered: 'This connection cannot be given standing consent.',
        failed: 'The setting could not be changed. Please try again.',
      },
      rolePins: {
        notHeld: 'You no longer hold that role in this module.',
        failed: 'The role could not be changed. Please try again.',
      },
      disconnect: 'Disconnect',
      disconnectConfirm: '“{client}” loses access to {workspace} at once. To use it again, connect it anew from the client.',
      disconnected: '{client} disconnected.',
      disconnectFailed: 'The connection could not be ended. Please try again.',
    },
    passkeys: {
      removeConfirm: 'This passkey will be removed. The device that uses it can no longer sign in with it.',
      title: 'Passkeys',
      hint: 'A passkey signs you in with your device — fingerprint, face or PIN. Nothing to remember and nothing to phish.',
      none: 'No passkeys yet.',
      add: 'Add a passkey',
      remove: 'Remove',
      unnamed: 'Passkey',
      lastUsed: 'Last used {when}',
      neverUsed: 'Never used',
      added: 'Passkey added.',
      removed: 'Passkey removed.',
      addFailed: 'The passkey could not be added. Please try again.',
      removeFailed: 'The passkey could not be removed. Please try again.',
      alreadyRegistered: 'This device already has a passkey for lumi.',
      lastMethod: 'You can\'t remove your only sign-in method. Set a password or add another passkey first.',
      lostDevice: 'Lost a phone or another device? Remove its passkey from this list — that is what locks it out of your account.',
    },
    currentPassword: 'Current password',
    phone: {
      title: 'Sign-in number',
      hint: 'Your account signs in with a passkey. This number receives your invitation and recovery links.',
    },
    email: {
      title: 'Sign-in address',
      hint: 'The address you sign in with. Changing it also changes the address your workspaces show for you.',
      newLabel: 'New address',
      submit: 'Request change',
      pending: 'Waiting for confirmation at {email}. Follow the link we sent there to finish the change.',
      cancel: 'Cancel this change',
      cancelled: 'The pending address change was cancelled.',
      sent: 'Check the new mailbox. Nothing changes until you follow the link we sent there.',
      refused: 'That address cannot be used for this account. One of your workspaces restricts sign-in addresses to its own domains.',
      failed: 'The change could not be requested. Please try again.',
      ssoManaged: 'Your sign-in address is managed by your identity provider.',
    },
    password: {
      title: 'Password',
      hint: 'Changing your password signs you out everywhere else.',
      newLabel: 'New password',
      confirmLabel: 'Repeat new password',
      submit: 'Change password',
      changed: 'Your password has been changed. Other devices have been signed out.',
      wrongCurrent: 'The current password is not correct.',
      failed: 'The password could not be changed. Please try again.',
      tooShort: 'The new password must be at least 8 characters.',
      mismatch: 'The two new passwords do not match.',
      ssoOnly: 'This account signs in through your identity provider, so it has no password. You can add one as a fallback — we will email you a link.',
      sendLink: 'Email me a link to set a password',
      linkSent: 'If a password can be set for this account, a link is on its way.',
      linkFailed: 'The request could not be sent. Please try again.',
    },
    confirmEmail: {
      title: 'Confirm your new address',
      working: 'Confirming…',
      success: 'Your sign-in address has been changed. Please sign in again with the new address.',
      goToLogin: 'Go to sign-in',
      backToAccount: 'Back to account & security',
      missingToken: 'This link is incomplete. Please open it directly from the email.',
      invalid: 'This confirmation link is invalid or has expired.',
      unavailable: 'This change can no longer be applied. Please request it again from your account page.',
    },
    // The undo half (#420): the notice mailed to the address being LEFT
    // BEHIND carries this link, and it only ever restores the status quo.
    cancelEmailChange: {
      title: 'Cancel the address change',
      working: 'Cancelling…',
      success: 'The change was cancelled. You keep signing in with your current address.',
      goToLogin: 'Go to sign-in',
      missingToken: 'This link is incomplete. Please open it directly from the email.',
      invalid: 'This link is invalid or has expired — there is no change left to cancel.',
    },
    title: 'Account & security',
    identities: 'Linked sign-in methods',
    noIdentities: 'No external sign-in methods linked.',
    unlink: 'Remove',
    // #882: removing a linked sign-in method asks first, as removing a passkey does.
    unlinkConfirm: 'This sign-in method is removed. You can no longer sign in to your account with it.',
    linkGoogle: 'Link Google',
    linkMicrosoft: 'Link Microsoft',
    linkApple: 'Link Apple',
    lastMethod: 'You can\'t remove your only sign-in method. Set a password first.',
    linked: 'Sign-in method linked.',
    unlinked: 'Sign-in method removed.',
    linkFailed: 'Linking failed.',
  },
  workspace: {
    title: 'Workspace',
    subtitle: 'Choose a tenant and module',
    brandName: 'lumi-HR Workspace',
    themeDark: 'Switch to dark mode',
    themeLight: 'Switch to light mode',
    welcomeTitle: 'Welcome to Workspace',
    welcomeSubtitle:
      'Select your organization tenant and choose an application module to begin your work.',
    availableModules: 'Available Modules',
    comingSoonSection: 'Coming Soon to lumi-HR',
    adminMode: 'Admin Mode',
    aiUsage: {
      title: 'AI usage',
      pageIntro: 'Whether lumi is available in this workspace — and what the AI has used this month.',
      loading: 'Loading usage…',
      remaining: '{n} remaining',
      periodLabel: 'Billing period',
      historyTitle: 'Billing periods',
      usedThisPeriod: 'Used this billing period',
      split: '{included} included · +{upgrades} from quota upgrades',
      resetsOn: 'Resets on {date}',
      periodFrom: 'Billing period from {date}',
      nearLimit: 'This billing period’s AI quota is almost used up. A quota upgrade under Billing raises it right away.',
      unlimited: 'No AI quota applies to this workspace.',
      ownKey: 'This workspace runs lumi on its own AI provider key, so it has no AI quota.',
      noUsageYet: 'No AI usage yet in this billing period.',
      upgradeLink: 'Add a quota upgrade under Billing',
      modules: {
        ats: 'Recruiting',
        time: 'Time',
        time_tracking: 'Time',
        apprentice: 'Ausbildung',
        apprenticeship: 'Ausbildung',
        assistant: 'lumi assistant',
        mcp: 'External agents',
      },
    },
    active: 'Active',
    systemAdmin: 'System Admin',
    user: 'User',
    admin: 'Admin',
    comingSoon: 'Coming soon',
    noTenants: 'No tenants available',
    createViaCheckout: 'Create a workspace through the checkout',
    loadFailed: 'Failed to load tenants',
    retry: 'Try again',
    logout: 'Logout',
    noAccess: 'No access',
  },
  support: {
    title: 'Support requests',
    myTickets: 'My requests',
    myTicketsTitle: 'My support requests',
    inbox: 'Inbox',
    inboxTitle: 'Support inbox',
    inboxSubtitle: 'Tickets across the tenants you support',
    ticket: 'Ticket',
    newTicket: 'New support request',
    newTicketTitle: 'New support request',
    newTicketIntro: 'Describe your issue and we will get back to you.',
    sent: 'Your request has been sent.',
    goToMyRequests: 'Go to my support requests',
    subject: 'Subject',
    subjectPlaceholder: 'Brief summary',
    message: 'Message',
    messagePlaceholder: 'Describe what happened…',
    consentLabel: 'I acknowledge my data is processed to handle this request',
    send: 'Send',
    reply: 'Reply',
    markComplete: 'Mark complete',
    reopen: 'Reopen',
    replyPlaceholder: 'Write a reply…',
    escalate: 'Escalate',
    escalated: 'Escalated',
    deescalate: 'De-escalate',
    deescalateBlocked: 'Support access is still open on this ticket. Revoke it first, then de-escalate.',
    close: 'Close',
    statusLabel: 'Status',
    module: 'Module',
    tenant: 'Tenant',
    requester: 'Requester',
    updated: 'Updated',
    backToTickets: 'Back to my requests',
    status: {
      open: 'Open',
      in_progress: 'In progress',
      answer: 'Answered',
      closed: 'Closed',
    },
    author: {
      user: 'You',
      support: 'Support',
      systemAdmin: 'System Admin',
    },
    empty: {
      myTitle: 'No tickets yet',
      myBody: 'When you create a support ticket it will appear here.',
      inboxTitle: 'Nothing to support',
      inboxBody: 'You have no support tickets in your tenants right now.',
      notFoundTitle: 'Ticket not available',
      notFoundBody: 'This ticket could not be loaded. It may have been removed or you may not have access.',
    },
  },
  notifications: {
    nav: 'Notifications',
    title: 'Notifications',
    subtitle: 'Things that need your attention.',
    settings: 'Settings',
    backToWorkspace: 'Back to workspace',
    area: 'Area',
    areaAll: 'All areas',
    status: 'Status',
    statusAll: 'All',
    statusUnread: 'Unread',
    statusRead: 'Read',
    perPage: 'Per page',
    markRead: 'Mark read',
    markUnread: 'Mark unread',
    markAllRead: 'Mark all read',
    loading: 'Loading…',
    empty: 'You’re all caught up.',
    pageInfo: '{from}–{to} of {total}',
    prev: 'Previous',
    next: 'Next',
    retentionHint: 'Notifications are kept for 6 months.',
    settingsTitle: 'Configure Notifications per Area',
    settingsSubtitle: 'Choose how you want to be notified for each area.',
    // #445 M4 packet 2: the settings screen must say plainly what it cannot
    // switch off, and what a switched-off Area still lets through.
    mustReachIntro: 'A few notices about security, money, contracts, your workspace and legal deadlines always reach the people they concern — they also appear in the bell.',
    // A phone-only account (issue 698): no address, so the mail half never goes out.
    noMailAddress: 'Your account has no e-mail address, so notices reach you in the app (and as a push notification where you allowed it). “{mailMode}” therefore works like “{inAppMode}”: nothing is sent by e-mail.',
    mustReachStillReaches: 'Even with this switched off, {list} will still reach you when they concern you.',
    mandatoryReason: {
      billing: 'Cannot be turned off: money you owe must reach the responsible person.',
      legal: 'Cannot be turned off: contract changes must reach you, as our terms promise.',
    },
    mustReachFamily: {
      identity: 'security notices',
      money: 'billing notices',
      legal: 'contract notices',
      workspace: 'workspace status notices',
      privacy: 'privacy deadline reminders',
      participation: 'employee representation requests',
    },
    backToList: 'Back to notifications',
    modeOff: 'Not at all',
    modeInApp: 'In app only',
    modeMailInApp: 'Mail and in app',
    saved: 'Saved.',
    saveError: 'Could not save. The setting has been reset.',
    openTarget: 'Open the linked item',
    opening: 'Opening…',
    openFailed: 'We couldn’t open that notification.',
    areas: {
      system: 'System',
      timecard: 'Time',
      ats: 'Recruiting',
      apprentice: 'Ausbildung',
      people: 'People',
      support: 'Support',
      postings: 'Job postings',
      // Subscription, invoices, payment problems. A MANDATORY Area — the
      // row renders without a mode selector (review finding 3).
      billing: 'Billing',
      // #373 M5: the contract-rollout notices (AGB, AVV, subprocessors). A
      // MANDATORY Area — the row renders without a mode selector.
      legal: 'Contract documents',
    },
  },
  homebase: {
    // The owner's own details, asked for on Suite Home until given (issue 812); skipping asks first.
    ownDetails: {
      save: 'Save my details',
      later: 'Later',
      saved: 'Thanks — your details are saved.',
      skip: {
        title: 'Skip your details?',
        body: 'Until you fill in your details, your own record stays unconfirmed in People. That means: no employee data such as contract, leave or payroll can be added to it, you cannot be set as anyone’s manager, and you show up as “unconfirmed” in People. You can fill them in any time — this reminder comes back tomorrow.',
        fill: 'Fill in now',
        skip: 'Skip for now',
      },
    },
    // The quiet "set up Lumi" panel on Suite Home: one fixed table of entries,
    // each a title + one benefit line; the rows are looked up per entry key.
    setup: {
      title: 'Set up lumi',
      subtitle: 'None of this is required. All of it pays off once you\'re ready.',
      hide: 'I don\'t need this anymore',
      viewAll: 'See everything lumi can do',
      rareHeading: 'Needed less often',
      page: {
        title: 'What lumi can do.',
        subtitle: 'Everything you can set up. The most important first, the rest when you need it.',
        tipsTitle: 'Tips on the home page',
        tipsShown: 'The panel sits at the top of your home page until you no longer need it.',
        tipsHidden: 'You hid the panel. Bring it back here.',
        emptyTitle: 'Nothing to set up for you here.',
        emptyBody: 'Whatever can be set up is taken care of by the people responsible in your team. Ask them if something is missing.',
      },
      sections: {
        suite: 'Home',
        ats: 'Recruiting',
        time: 'Time',
        people: 'People',
        apprentice: 'Ausbildung',
      },
      entries: {
        login_branding: { title: 'Design your sign-in page', hint: 'Logo and colours, so your team can tell it\'s the right place.' },
        portal_design: { title: 'Design your careers portal', hint: 'How applicants first see your company.' },
        email_templates: { title: 'Email templates', hint: 'Confirmation, invitation, rejection: write once, use every time.' },
        job_posting_settings: { title: 'Job posting details', hint: 'Logo and contact person. External job boards need both.' },
        work_time_models: { title: 'Working time models', hint: 'Who works how many hours on which days.' },
        company_holidays: { title: 'Company closures', hint: 'Days when the business is closed.' },
        data_import: { title: 'Import people', hint: 'Bring in your existing lists instead of typing everything.' },
        company_profile: { title: 'Company profile', hint: 'Address and key facts lumi uses in many places.' },
        org_units: { title: 'Organisational units', hint: 'Departments and locations that a lot gets sorted by.' },
        payroll_handover: { title: 'Payroll handover (DATEV)', hint: 'lumi delivers the hours, your tax advisor calculates pay.' },
        training_occupations: { title: 'Training occupations', hint: 'Which occupations you train.' },
        workflows: { title: 'Workflows', hint: 'Your own steps and approvals in the application process.' },
        application_forms: { title: 'Application forms', hint: 'Which questions applicants answer.' },
        offer_templates: { title: 'Offer templates', hint: 'Prepare contract offers with placeholders.' },
        custom_fields: { title: 'Custom fields', hint: 'Extra details on jobs and applications.' },
        booking_types: { title: 'Booking types', hint: 'What can be booked besides working time.' },
        leave_types: { title: 'Absence types', hint: 'Vacation, sickness and your own kinds.' },
        api_settings: { title: 'Interface (API)', hint: 'Pass times to your own programs via the API. The payroll handover is under People.' },
        time_clock: { title: 'Time clock', hint: 'Open it in the browser of a tablet on site: each person signs in there with their own lumi login and clocks in or out.' },
        sso: { title: 'Single sign-on', hint: 'Sign in with your company account.' },
        onboarding_templates: { title: 'Onboarding templates', hint: 'Checklists for the first days of new colleagues.' },
        recruiting_options: { title: 'Option lists', hint: 'Salutations, contract types and work locations your team picks from.' },
      },
    },
    greeting: { morning: 'Good morning', day: 'Hello', evening: 'Good evening' },
    // The date line under the greeting ("Monday, 21 September · Week 39",
    // workspace-start M2) replaced the static subtitle. Weekday + day/month come
    // from Intl; this key only supplies the week label.
    dateLine: { week: 'Week {n}' },
    open: 'Open',
    recents: 'Recent',
    recentKinds: { report: 'Weekly report', job: 'Job', application: 'Application' },
    moreActions: 'More actions',
    clockedInSince: 'Clocked in since {time}',
    clockedOut: 'Clocked out',
    clockOut: 'Clock out',
    clockIn: 'Clock in',
    projectRunning: 'Project time running',
    projectRunningSince: 'Since {time}',
    startProject: 'Start a project',
    // The clock tile for a person freed from booking working time (issue 981): the sentence, then the way to a project.
    workTimeFree: 'Your contract means you book project time only, not working time.',
    // The Stempeluhr's project choice once clocked in (Zeit's "Woran arbeitest du?"): the tile's field and its sheet.
    clockProject: {
      title: 'What are you working on?',
      none: 'No project',
      noneSub: 'Counts as “Unassigned”',
      choose: 'Choose project and activity',
      fieldLabel: 'What are you working on? Now: {current}',
      runningWith: '{activity} · since {time}',
      runningSince: 'Since {time}',
      activityStep: 'Which activity?',
      back: 'All projects',
      defaultActivity: 'Default',
      search: 'Search projects',
      noMatch: 'No project found',
      close: 'Close',
    },
    provisioning: 'Setting up — this can take a minute.',
    preferences: 'Preferences',
    seeMyOrg: 'See my org',
    // What each module Lumi still sells actually does (issue 261 M2). Longer than the
    // launcher's one-liners (app-kit's `lumiModules.<key>.description`): this is one
    // app, so the home can say it in the module's own vocabulary.
    teaser: {
      about: {
        ats: 'Job postings, applications and the hiring pipeline — from the first CV to the signed contract.',
        time_tracking: 'Clock-in and clock-out, work-time accounts, absences and overtime, ready for the working-time record.',
        apprenticeship: 'Digital training records, training plans and evaluations for every apprentice in the company.',
      },
      heading: 'More modules',
    },
    empty: 'Nothing here yet — open the module to get started.',
    unavailable: 'Can’t be loaded right now.',

    goToModule: 'Go to {module}', expand: 'Expand {module}', collapse: 'Collapse {module}',
    themeLight: 'Light',
    themeDark: 'Dark',
    people: { selfService: 'My data' },
    myDocuments: {
      title: 'My documents',
      downloadFailed: 'Download failed – please try again later.',
      showAll: 'Show all ({count})',
      showLess: 'Show less',
      handIn: 'Hand in a document',
      empty: 'Nothing here yet. You can hand in a certificate, attestation, sick note or ID copy for your file.',
      kindLabel: 'What is it?',
      fileLabel: 'File – choose one or take a photo',
      send: 'Send',
      cancel: 'Cancel',
      handedIn: 'Handed in on {date} · awaiting HR',
      accepted: 'Handed in on {date} · accepted',
      sentBack: 'Sent back on {date}: “{reason}”',
      handInAgain: 'Hand in again',
      dismiss: 'Dismiss',
      error: {
        file_refused: 'This file could not be accepted – please try another file.',
        file_too_large: 'This file is too large – the limit is 25 MB.',
        file_empty: 'This file is empty – please choose another file.',
        file_missing: 'Please choose a file first.',
        quota_exceeded: 'Your file has reached its storage limit – please contact HR.',
        invalid_category: 'Please choose what kind of document it is.',
        not_possible: 'Handing in is not possible right now – please contact HR.',
        generic: 'Handing in failed – please try again later.',
        scan_unavailable: 'The file could not be checked right now. Please try again shortly.',
      },
      category: {
        contract: 'Employment contract',
        contract_amendment: 'Contract amendment',
        certificate: 'Reference / certificate',
        attestation: 'Attestation',
        payslip: 'Payslip',
        health_certificate: 'Sick note / medical certificate',
        identification: 'ID document',
        other: 'Other',
      },
    },
    admin: {
      users: 'Users',
      usersMoved: {
        title: 'User management has moved to People',
        redirecting: 'Opening People…',
        noPeople: 'Who has access to this workspace is managed on each person in People. People is not available to you here — ask the workspace owner.',
      },
    },
    verwaltung: { heading: 'Administration' },
    nav: { home: 'Dashboard', billing: 'Billing', logout: 'Log out', language: 'Language', administration: 'Administration', sso: 'Single Sign-On', audit: 'Audit Log', supportAccess: 'Support Access', aiUsage: 'AI usage', dataProcessing: 'Data processing', dataRequests: 'Data subject requests', legalNotices: 'Legal notices', statutoryRules: 'Statutory rules', trainingOccupations: 'Training occupations', externalAgents: 'External AI agents', setup: 'Set up' },
    // The running week against its Sollstunden (#403). `hoursUnit` is a
    // separate key because the figures are formatted with Intl and the unit
    // trails them in every language this app ships.
    week: {
      title: 'This week',
      hoursUnit: 'h',
      balance: 'Working-time account, as of {date}: {balance}',
      balanceHint: 'Hours worked minus hours owed up to that day.',
      noTarget: 'No weekly hours agreed.',
      trustBased: 'You work on trust-based working time: you record when you start and finish, and there is no target and no time account for you.',
      projectsMore: 'and {n} more',
      days: 'Days of the week',
    },
    heute: {
      clock: 'Clock',
      attention: 'Needs me',
      news: 'New',
      leaveRemaining: 'Leave remaining',
      days: 'Day | Days',
      kind: { interview: 'Interview', leave: 'Absences', holiday: 'Public holiday', exam: 'Exam', station: 'Rotation' },
    },
    facts: {
      ats: { total_jobs: 'Jobs', total_applications: 'Applications', open_tasks: 'Open tasks', new_applicants: 'New applicants', create_job: 'Create job', add_application: 'Add application', create_task: 'Create task', pipeline: 'Pipeline', pipeline_closed: '{n} closed', stage_other: 'Other' },
      time: { week_hours: 'Hours this week', today_hours: 'Hours today', timesheet: 'My timesheet', request_leave: 'Request leave', approvals: 'Approvals', team: 'Team', pending_approvals: 'Open approvals', leave_remaining: 'Leave remaining', proof_open: 'Proof missing' },
      apprentice: { waiting: 'To review', active: 'Apprentices', overdue: 'Missing weeks', this_week: 'This week', badges: 'Skills', write: 'Log this week', skills: 'Skill wall', review: 'Review reports', onboard: 'Add apprentice', reports: 'Report books', report_approved: 'Approved', report_submitted: 'To review', report_rejected: 'Returned', report_missing: 'Missing' },
    },
  },
  preferences: {
    appearance: 'Appearance',
    theme: 'Theme',
    themeHint: 'Light or dark interface.',
    language: 'Language',
    languageHint: 'The language used across the workspace.',
    attributions: 'Open data attributions',
    placesCredit: 'Place data © {source}, licensed under {licence}.',
    bankCodesCredit: "Bank codes: source {bundesbank}; Austrian bank codes © {oenb}, licensed under {licence}; Swiss and Liechtenstein bank codes © {six}.",
    healthInsurersCredit: "Health insurers: social insurance master data file of the {gkv}.",
  },
  onboarding: {
    actions: { add: 'Add', back: 'Back', next: 'Next', remove: 'Remove' },
    modules: {
      ats: 'Recruiting',
      time_tracking: 'Time',
      apprenticeship: 'Ausbildung',
    },
    fork: {
      // The headline is two keys: the lead and `titleMark`, the final beat that sits
      // on the highlighter stroke. The mark is always the LAST beat of the sentence.
      eyebrow: 'Welcome to lumi',
      title: "Glad you're",
      titleMark: 'here.',
      subtitle: 'How do you want to start? You can switch any time.',
      // From a live demo the same page is the way out of it.
      eyebrowDemo: 'Leaving the demo',
      titleDemo: 'Enough exploring.',
      titleDemoMark: 'Now for real.',
      subtitleDemo: 'With the guided setup the demo company stays until you finish.',
      reassure: 'Not sure? Start with the demo – you can switch to the real setup any time.',
      seedFailed: 'Preparing the demo data did not finish. Please try again.',
      seedFailedNotEmpty:
        'The demo needs empty modules, but one of yours already has data. Remove it first, or choose “Set up your suite” instead.',
      // #364 F19: why the fork opened mid-Finish. The wizard hands an admin back
      // here when the AVV is still unsigned; landing with nothing said is what
      // made a refused Finish read as a silent drop.
      avvRequiredNotice:
        'Setup cannot be completed until the Data Processing Agreement (AVV) is accepted. Accept it here and you go straight back to the setup guide.',
      // #364 F21 / ADR 0032 Decision 2: the fork is now also reached FROM a live
      // demo, so it needs the wizard's way out. Leaving costs nothing.
      backToDemo: 'Back to the demo',
      demo: {
        eyebrow: 'Try it out',
        title: 'Explore with demo data',
        body: 'We fill your modules with one coherent, fictional company. Click through everything without breaking anything.',
        modulesHint: 'Booked modules are always included.',
        cta: 'Explore the demo',
      },
      guided: {
        eyebrow: 'Set up',
        title: 'Guide me through setup',
        body: 'Step by step to your real suite. Pause, skip, or go to the demo first – anything goes.',
        bodyDemo: 'Step by step to your real suite. If you close the window, you are back in the demo.',
        pointSaved: 'Your progress is saved',
        pointOptional: 'Every step is optional',
        cta: 'Guide me through it',
      },
      empty: {
        eyebrow: 'Do it yourself',
        titleFresh: 'Start empty',
        titleDemo: 'Discard the demo and start empty',
        bodyFresh: 'A blank canvas. You set everything up bit by bit, at your own pace.',
        bodyDemo: 'The demo company is deleted and you start from an empty base.',
        warnDemo: 'This happens right away and cannot be undone.',
        cta: 'Start empty',
      },
    },
    // The demo exit gate (#409 M3): what the workspace has booked, what it could
    // add and at what price, both ends of the trial and what is charged when —
    // shown once per fork visit when an admin leaves a live demo.
    exitGate: {
      title: 'Before you leave the demo',
      intro: 'This is what your workspace is booked for and where your trial stands. Add a module now, or later from Billing — setting up works either way.',
      booked: { title: 'Booked' },
      // The colleagues invited from the demo bar, named before the owner ends the demo
      // (issue 669); `fallback` stands in when the roster could not be read.
      guests: {
        title: 'Colleagues you invited to the demo',
        lead: 'These colleagues lose access when the demo ends:',
        fallback: 'Colleagues you invited to the demo lose access when it ends.',
        people: 'Your real team is invited from People once your workspace is set up.',
      },
      // What only the gate can say about a module booked at it (#409 F12): whether
      // its setup-guide section is there yet. The booking rows themselves are the
      // billing section's.
      bookedHere: {
        settingUp: 'Setting up {module}…',
        booked: '{module} is booked and has its own section in the setup guide.',
        bookedPending: '{module} is booked. Its section appears in the setup guide as soon as the module is ready.',
      },
      noManage: 'Only the workspace owner, a billing manager or a workspace admin can book modules. You can still continue without booking.',
      enforcement: 'There is an outstanding payment on this workspace. Booking is paused until it is settled — setting up your suite is not.',
      factsUnavailable: 'We could not load your booking right now. You can still continue; everything is on the Billing page.',
      trial: {
        title: 'Your trial',
        range: 'Your trial started on {start} and ends on {end}.',
        endOnly: 'Your trial ends on {end}.',
        rangeFree: 'Free from {start} until {end}.',
        endOnlyFree: 'Free until {end}.',
        charge: 'From {end}, {amount} per month is charged for what you have booked, at the start of each billing month.',
        chargeNoTotal: 'From {end}, your booking is charged monthly, at the start of each billing month.',
        firstCharge: 'The first charge is on {date}.',
        demoCounted: 'The time you spent in the demo counted toward your trial.',
        over: 'Your trial has ended and your booking is billed monthly.',
        monthly: 'That is {amount} per month.',
        nextCharge: 'The next charge is on {date}.',
      },
      continue: 'Continue to setup',
    },
    // AVV acceptance gate (issue 332): shown before "Guide me through it" or
    // "Start empty" proceed (including a demo-abandon), never before "Explore
    // with demo data". Recorded server-side by POST .../onboarding/avv/accept.
    avv: {
      modalTitle: 'Accept the Data Processing Agreement (AVV)',
      modalBody:
        'Setting up your suite with real data means lumi processes personal data on your behalf. Before continuing, please read and accept our current Data Processing Agreement (AVV).',
      modalLinkText: 'Read the full AVV',
      modalCheckboxLabel: 'I have read and accept the current AVV.',
      modalCancel: 'Not yet',
      modalAccept: 'Accept and continue',
    },
    preparing: {
      first: {
        title: 'Preparing your demo.',
        body: 'We are seeding a coherent, fictional company across your modules. This only takes a moment.',
      },
      reset: {
        title: 'Resetting your demo.',
        body: 'We are clearing the demo company and building it again from scratch. Your workspace and your colleagues are untouched. This only takes a moment.',
      },
      stuck: {
        note: 'This is taking longer than expected.',
        cta: 'Set up manually instead',
      },
    },
    nonAdmin: {
      title: 'Almost',
      titleMark: 'there.',
      body: 'Your administrator is still setting up the suite. As soon as that is done, you will come straight in here.',
    },
    wizard: {
      title: 'Set up your suite',
      subtitle: 'Everything here is optional and your progress is saved. Set up what you like, finish the setup, or return to the demo by closing this window.',
      subtitleCommitting: 'Your setup is being applied. If a step fails, you can skip it and set it up later in the settings.',
      crossRef: 'Used in {modules} — change it later in {owner}.',
      crossRefOwnerDefault: 'People',
      recovery: '“{step}” could not be applied. Check your entries there, then finish again.',
      details: {
        step: 'Your details',
        title: 'Your details',
        subtitle: 'Your own record in People. Check your name — saving it confirms your record.',
        loading: 'Loading your record…',
        confirmed: 'Your record is confirmed as {name}. You can change it later in People.',
        unavailable: 'Your record could not be loaded right now. You can confirm it later in People.',
        firstName: 'First name',
        lastName: 'Last name',
        required: 'Enter your first and last name.',
        hq: {
          title: 'Your headquarters',
          why: 'Lumi can\'t work without a location: it needs your headquarters as your first location, for example for your job postings.',
          street: 'Street',
          postalCode: 'Postal code',
          city: 'City',
          country: 'Country',
          countryNone: 'No country selected',
          required: 'Enter the street, postal code, city and country of your headquarters.',
        },
      },
      general: {
        title: 'General',
        company: {
          title: 'Company profile',
        },
        name: {
          label: 'Company name',
          hint: 'Prefilled from checkout — edit if needed. Required.',
          required: 'Company name is required.',
        },
        profile: {
          industry: 'Industry',
          employeeCountRange: 'Company size',
          employeeCountPlaceholder: 'e.g. 50–200',
          foundedYear: 'Founded',
          foundedYearPlaceholder: 'e.g. 2010',
          legalForm: 'Legal form',
          websiteUrl: 'Website',
        },
        place: {
          hint: 'Pick a town from the list as the location’s name — or type your own name and confirm with Enter or +.',
        },
        org: {
          title: 'Organisation structure',
          subtitle: 'Departments, locations and cost centres your people belong to.',
          departments: 'Departments',
          locations: 'Locations',
          costCenters: 'Cost centres',
          empty: 'None yet',
          addDepartment: 'New department',
          addLocation: 'New location, e.g. Berlin',
          addCostCenter: 'New cost centre',
        },
      },
      finish: {
        cta: 'Finish setup',
        step: 'Finish',
        title: 'Finish your setup',
        body: 'Finishing applies your answers. You can change everything later in each module.',
        nextTitle: 'Invite your team next',
        nextBody: 'After setup, open the admin area to invite the first users and assign their roles.',
        demoWarning: 'Finishing wipes the demo company and any users invited while exploring it.',
      },
      sections: {
        ats: {
          title: 'Recruiting',
          subtitle: 'Set up how applications and hiring emails work.',
          fields: {
            fromName: { label: 'Sender name', hint: 'Shown on emails to candidates.' },
            signature: { label: 'Email signature', hint: 'Appended to outgoing candidate emails.' },
          },
          jobPosting: {
            title: 'Job posting',
            hint: 'The candidate contact and logo shown on external job ads (optional — you can set this up later, too).',
            contactName: 'Contact name',
            contactEmail: 'Contact email',
            logo: 'Job-ad logo',
            uploadLogo: 'Upload logo',
            removeLogo: 'Remove',
            rePick: 'Your logo was not uploaded yet — pick it again.',
            contactEmailInvalid: 'Please enter a valid email address.',
          },
          workflow: {
            title: 'Hiring workflow',
            participantsQuestion: 'Who is involved in job ads and applications?',
            worksCouncil: 'Works council',
            sbv: 'Representative body for employees with disabilities (SBV)',
            legalSignOff: 'Legal — releases a job ad before it is published',
            assessmentCentreQuestion: 'Do you use assessment centres?',
            assessmentCentre: 'Yes, we use assessment centres',
            hint: 'You can edit and update the process later in the ATS at any time.',
          },
        },
        time_tracking: {
          title: 'Time',
          subtitle: 'Booking types and working-time rules.',
          fields: {
            bookingTypes: { label: 'Booking types', hint: 'Add the booking types you use.', placeholder: 'e.g. Work' },
          },
          companyHolidays: {
            title: 'Company holidays',
            hint: 'Days the company is closed – for example half a day on Christmas Eve and New Year’s Eve. No target hours are due on these days. For each entry you decide whether it is taken from the holiday entitlement; you can change this later in the settings of Time.',
            empty: 'No company holidays entered yet.',
            suggestions: 'Suggestions:',
            christmasEve: 'Christmas Eve',
            newYearsEve: 'New Year’s Eve',
            name: 'Name',
            namePlaceholder: 'e.g. Between the years',
            from: 'From',
            to: 'To (optional)',
            halfDay: 'Half day',
            yearly: 'Every year',
            deductsFromLeave: 'Take from leave',
            rangeReversed: 'The end date is before the start date.',
          },
        },
        apprenticeship: {
          title: 'Ausbildung',
          subtitle: 'Training professions.',
          fields: {
            professions: { label: 'Training professions', hint: 'The apprenticeship professions you train.', placeholder: 'e.g. IT specialist' },
          },
        },
        people: {
          title: 'People',
          subtitle: 'Shared vocabulary used across the suite.',
          fields: {
            salutations: { label: 'Salutations', hint: 'Add salutations on top of the standard set.', placeholder: 'e.g. Mx' },
            contractTypes: { label: 'Contract types', hint: 'Add contract types on top of the standard set.', placeholder: 'e.g. Working student' },
            workTimeModels: { label: 'Working-time models', hint: 'Add models on top of the standard set.', placeholder: 'e.g. Job sharing' },
            workLocationTypes: { label: 'Work locations', hint: 'Add work-location types on top of the standard set.', placeholder: 'e.g. Field service' },
          },
        },
      },
    },
    commit: {
      wiping: {
        title: 'Clearing the demo…',
        body: 'Removing the demo company so your real setup starts clean.',
      },
      applying: {
        title: 'Setting up your suite…',
        body: 'Applying your answers. This only takes a moment.',
      },
      steps: {
        company: 'Company profile',
      },
      error: 'Something went wrong while applying your setup.',
      retry: 'Retry',
      backToSetup: 'Back to setup',
      skipStep: 'Finish without this step',
      skipped: 'Skipped: {steps}. You can set this up any time in the settings.',
      resume: 'Resume setup',
      stalled: 'This is taking longer than expected.',
      wipeFailed: 'The demo could not be cleared. Retry to continue.',
      logoMissing: 'Your logo was not uploaded (lost on reload) — you can add it later in the job posting settings.',
      workflowFailed: 'Your hiring chain could not be generated, so your workspace starts on the standard chain. Everything else was applied — open the workflow settings in Recruiting to run the builder again.',
      workflowDrafted: 'Your recruiting process was saved as a draft because {roles} has nobody assigned yet. Open the workflow settings in Recruiting to activate it once the role is staffed.',
      workflowRoles: {
        ats_works_council: 'the works council',
        ats_sbv: 'the representative body for employees with disabilities',
        ats_legal: 'the legal team',
      },
      continue: 'Continue to workspace',
    },
  },
  tenants: {
    switcherLabel: 'Switch workspace',
    supportAccessBadge: 'Support',
    supportAccessUntil: 'Temporary support access until {until}',
    supportAccessBanner: 'lumi support currently has temporary access to this workspace.',
    supportAccessReview: 'Review',
  },
  // The my-data section (issue 241, D20/D21). Every sentence here is a claim
  // about what the system actually does with a request: it is recorded, it is
  // worked by people, and the person is told the outcome. Nothing here promises
  // a deletion, an instant download or a deadline — the only dates a person
  // sees are the ones privacy-api computed for their own request.
  dataProtection: {
    blockedErasure: {
      title: 'What is blocking a deletion',
      intro: 'Personnel documents with no retention date are kept indefinitely: nobody has decided how long they must be held, and an undecided document is never deleted automatically. They are listed here so the dates can be set.',
      sensitivePlaceholder: 'Sensitive document',
      categories: {
        contract: 'Contract',
        contract_amendment: 'Contract amendment',
        certificate: 'Certificate',
        attestation: 'Confirmation',
        warning: 'Warning',
        other: 'Other document',
      },
      outcomes: {
        refused: 'Deletion refused',
        partial: 'Partly deleted',
      },
      list: {
        title: 'Documents with no retention date',
        fixHint: 'The oldest are shown first. Set a retention date on the document in the personnel file to remove it from this list.',
        loading: 'Loading documents…',
        loadError: 'The list could not be loaded. Please try again — do not read this as “nothing found”.',
        empty: 'Every document in this workspace has a retention date.',
        uploadedLabel: 'Uploaded',
        truncated: 'Only the {count} oldest documents are shown. More are waiting for a retention date.',
      },
    },
    myData: {
      title: 'My data',
      intro: 'Here you can ask what this workspace holds about you, ask for it to be corrected, or ask for it to be deleted. Every request is recorded and answered by the people responsible for data protection in this workspace, and you can follow it here.',
      requests: {
        title: 'My requests',
        loading: 'Loading your requests…',
        loadError: 'Your requests could not be loaded. You can still make a request below.',
        empty: 'You have not made a request yet.',
        filedLabel: 'Made',
        dueLabel: 'Answer due',
        outcomeLabel: 'Result',
        awaitingRelease: 'Copy is being checked',
      },
      kinds: {
        access: 'Copy of my data',
        rectification: 'Correction',
        erasure: 'Deletion',
      },
      statuses: {
        received: 'Received',
        verifying: 'Identity being checked',
        in_progress: 'Being worked on',
        awaiting_subject: 'Waiting for your reply',
        completed: 'Completed',
        refused: 'Refused',
        withdrawn: 'Withdrawn',
      },
      outcomes: {
        erased: 'Data deleted',
        suppressed: 'Data deleted, minimum record kept',
        refused: 'Refused',
        partial: 'Partly done',
        answered: 'Answered',
      },
      actions: {
        access: {
          title: 'Ask for a copy of my data',
          body: 'You can ask for a copy of the data this workspace holds about you. It is not an immediate download: the copy is put together first, and you are told when it is ready.',
          where: 'A copy that is ready is never sent as an email attachment. You get an email, sign in to your lumi HR account and download it there.',
          cta: 'Ask for a copy',
          detailLabel: 'Anything you want to add (optional)',
          detailHint: 'For example, if you only want part of your data.',
        },
        rectification: {
          title: 'Ask for a correction',
          body: 'If something this workspace holds about you is wrong or out of date, you can ask for it to be corrected.',
          cta: 'Ask for a correction',
          detailLabel: 'What is wrong, and what should it say?',
          detailHint: 'Please describe the data as precisely as you can, so it can be found and checked.',
          selfTitle: 'Some of it you can change yourself',
          selfFields: 'Your name and form of address, your gender, your contact email, your phone number, your address, your nationality, your marital status and your language are yours to edit in your profile.',
          selfLink: 'Open my profile',
          askBody: 'For everything else — your date of birth, your employee number, contract, pay or tax data, your documents — use the request below.',
        },
        erasure: {
          title: 'Ask for my data to be deleted',
          body: 'You can ask this workspace to delete the data it holds about you.',
          cta: 'Ask for deletion',
          confirm: 'Your request is reviewed by the people responsible in this workspace. Data that has to be kept by law, or that is still needed for your employment, cannot be deleted. You are told what was deleted and what was kept.',
          detailLabel: 'Anything you want to add (optional)',
          detailHint: 'For example, which data you mean, if it is not all of it.',
        },
      },
      form: {
        submit: 'Send request',
        submitting: 'Sending…',
        cancel: 'Cancel',
        success: 'Your request has been recorded. You will find it above under “My requests”.',
        errorGeneric: 'Your request could not be sent. Nothing was recorded — please try again.',
        errorRateLimit: 'You have sent several requests in a short time. Please try again later.',
      },
    },
    export: {
      title: 'Export this workspace',
      intro: 'You can download a complete copy of everything this workspace holds, in a machine-readable form, whenever you want and without asking us.',
      contents: {
        title: 'What is in this file',
        body: 'The archive holds every person’s data in readable form and, alongside it, this workspace’s live access keys: the AI key, calendar links with their tokens, and the credentials of connected providers. The archive also includes an HR Open Standards (HR-JSON) document for People, Recruiting and Time — the shared format for taking your data to another provider.',
        handling: 'Keep it as carefully as you would keep those keys themselves. Anyone who obtains the file and the passphrase has everything in it.',
      },
      roleRequired: {
        title: 'This export needs the data protection role',
        body: 'The archive contains every file stored in this workspace — pay statements, medical certificates and identity documents among them — next to everyone’s personal data and this workspace’s live access keys.',
        hint: 'Owning the workspace is no longer enough to create one: whoever starts it must also hold the “Data Protection” role. That role is assigned with the other suite roles in user administration, and it can be assigned to you.',
      },
      start: {
        title: 'Start an export',
        body: 'Choose a passphrase. The archive is encrypted with it, and it is the only thing that opens it.',
        passphraseWarning: 'We never store your passphrase and cannot recover it. If you lose it, the archive can no longer be opened — there is no reset.',
        passphraseLabel: 'Passphrase',
        passphraseHint: 'At least {min} characters. Pick something long that you can store safely.',
        confirmLabel: 'Repeat passphrase',
        submit: 'Start export',
        submitting: 'Starting…',
        success: 'The export has been started. It is put together in the background and appears below once it is ready.',
        errorTooShort: 'The passphrase needs at least {min} characters.',
        errorMismatch: 'The two passphrases do not match.',
        errorAlreadyRunning: 'An export of this workspace is already running. Wait until it has finished before starting another.',
        errorTooMany: 'This workspace has started the maximum number of exports for now. Please try again later.',
        errorGeneric: 'We could not confirm that the export started. Check the list below before starting another one.',
      },
      list: {
        title: 'Exports of this workspace',
        loading: 'Loading exports…',
        loadError: 'The exports could not be loaded.',
        empty: 'This workspace has not been exported yet.',
        linkHint: 'A download link is only valid briefly. If it stops working, use the button again.',
        retentionHint: 'Every archive is deleted automatically when it expires — the date is shown next to it.',
        startedLabel: 'Started',
        expiresLabel: 'Available until',
        download: 'Download',
        preparing: 'Preparing…',
        downloadError: 'The download could not be prepared. The archive may no longer be there.',
        gone: 'The archive has been deleted. Start a new export if you need another copy.',
        failedHint: 'This export did not finish. You can start a new one.',
        runningHint: 'This export is still being put together.',
      },
      statuses: {
        requested: 'Requested',
        exporting: 'Being prepared',
        exported: 'Ready',
        failed: 'Failed',
        expired: 'Deleted',
      },
    },
    settingsPanel: {
      title: 'Data processing',
      body: 'You can ask to have your data deleted or corrected, or request a copy, {link}.',
      linkLabel: 'here',
    },
  },
  // Administration › Data subject requests (issue 789): the DSAR work list.
  dataRequests: {
    forbidden: {
      title: 'This list is for the data protection role',
      description: 'Data subject requests are worked by the workspace owner or the holder of the “Data Protection” role.',
    },
    intro: {
      title: 'Data subject requests',
      body: 'Requests from people about the data this workspace holds on them. Each must be answered within one month of receipt (Art. 12(3) GDPR). The deadline can be extended twice by one month if the request is complex — with a reason the person must be told.',
    },
    filters: {
      label: 'Show requests',
      open: 'Open',
      overdue: 'Overdue',
      dueSoon: 'Due within 7 days',
      closed: 'Answered recently',
    },
    empty: {
      title: 'No data subject requests',
      description: 'When someone asks about their data — in lumi, or by email, post or phone — the request appears here with its deadline.',
    },
    emptyFilter: 'No requests in this view.',
    truncated: 'Not every request fits on this page. The oldest deadlines are shown first.',
    badges: {
      overdue: 'Overdue',
      dueSoon: 'Due soon',
      legalHold: 'Legal hold',
      reviewReady: 'Ready for review',
      reviewReleased: 'Released',
      reviewDelivered: 'Delivered',
    },
    row: {
      received: 'Received',
      due: 'Answer due',
      closed: 'Closed',
      subject: 'About',
      channel: 'Arrived via',
      outcome: 'Result',
      extensions: 'Deadline extended {count} of 2 times',
      postponed: 'Reminders paused until {date}',
      vetoWindow: 'The erasure can still be stopped until {date}',
      reviewFlagged: 'Needs your decision: {flagged} of {total} entries',
      reviewNoneFlagged: 'No entries flagged',
      reviewEmpty: 'The copy contains no entries',
      reviewOpen: 'Review',
      reviewReleased: 'Released, being delivered',
      reviewDelivered: 'Delivered, closing',
    },
    legalHold: {
      tenant: 'A workspace-wide legal hold applies ({reason}).',
      subject: 'A legal hold covers this person ({reason}).',
      consequence: 'The data may not be erased. The lawful answer is a refusal that names this basis — the hold does not stop the deadline.',
    },
    automated: {
      pending: 'lumi answers this request itself: once you have checked the person’s identity and moved it to “Being worked on”, lumi puts together the copy of their data. It then waits for your review. After you release it, the person is told where to download it and the request closes on its own.',
      running: 'lumi is putting together the copy of this person’s data. It then waits for your review — the person is told where to download it only after you release it.',
    },
    detail: {
      show: 'Show who asked',
      hide: 'Hide details',
      loading: 'Loading details…',
      loadError: 'The details of this request could not be loaded.',
      subjectLabel: 'Name given',
      subjectEmail: 'Email address',
      subjectPhone: 'Phone number',
      requestDetail: 'What was asked',
      onBehalfReason: 'Recorded on their behalf because',
      extensionReason: 'Reason for the extension',
      outcomeBasis: 'Basis named',
      nothingOnFile: 'Contact',
      nothingOnFileValue: 'No name, address or message is on file for this request.',
    },
    kinds: {
      access: 'Copy of their data',
      rectification: 'Correction',
      erasure: 'Deletion',
      restriction: 'Restriction of processing',
      portability: 'Data portability',
      objection: 'Objection to processing',
    },
    statuses: {
      received: 'Received',
      verifying: 'Identity being checked',
      in_progress: 'Being worked on',
      awaiting_subject: 'Waiting for the person',
      completed: 'Completed',
      refused: 'Refused',
      withdrawn: 'Withdrawn',
    },
    outcomes: {
      erased: 'Data deleted',
      suppressed: 'Data deleted, minimum record kept',
      refused: 'Refused',
      partial: 'Partly done',
      answered: 'Answered in full',
    },
    subjectTypes: {
      person: 'An employee record',
      tenant_user: 'A member without an employee record',
      candidate: 'An applicant',
      unlinked: 'Not matched to a record',
    },
    channels: {
      'self-service': 'Filed in lumi by the person',
      'candidate-portal': 'Applicant portal',
      email: 'Email',
      post: 'Post',
      phone: 'Phone',
      unknown: 'Not recorded',
    },
    actions: {
      advance: {
        verifying: 'Check identity',
        in_progress: 'Start working on it',
        awaiting_subject: 'Wait for the person',
      },
      extend: 'Extend deadline',
      close: 'Close request',
      postpone: 'Pause reminders',
      veto: 'Open veto window',
      hold: 'Hold this person',
      restrict: 'Restrict processing',
      record: 'Record a request',
    },
    confirmAutomated: {
      title: 'Put together this person’s data?',
      body: 'Moving the request to “Being worked on” tells lumi to put together everything this workspace holds about the person. The copy does not go to them yet: it waits for your review, and the person is told where to download it only after you release it. Only do this once their identity is established.',
      confirm: 'Start',
    },
    toast: {
      advanced: 'Request moved to “{status}”.',
      extended: 'Deadline extended to {date}.',
      closed: 'Request closed.',
      postponed: 'Reminders paused until {date}.',
      vetoOpened: 'Veto window opened. The erasure waits 72 hours.',
      holdPlaced: 'Legal hold placed.',
      holdReleased: 'Legal hold released.',
      holdAttested: 'Legal hold confirmed for another six months.',
      restrictionPlaced: 'Processing restricted.',
      restrictionNotified: 'Notice recorded.',
      restrictionLifted: 'Restriction lifted.',
      recorded: 'Request recorded.',
    },
    extend: {
      title: 'Extend the deadline',
      body: 'Art. 12(3) GDPR allows two extensions of one month each where a request is complex or there are many. Tell the person within the first month, with the reason.',
      remaining: 'Extensions left: {count}',
      reasonLabel: 'Reason for the delay',
      reasonHint: 'Recorded with the request. Give the person the same reason.',
      submit: 'Extend by one month',
      errorReason: 'Please give the reason for the delay.',
    },
    close: {
      title: 'Close the request',
      body: 'A closed request cannot be reopened: it is the record of how and when the workspace answered.',
      statusLabel: 'How was it closed?',
      outcomeLabel: 'What was done',
      outcomePlaceholder: 'Choose…',
      basisLabel: 'Statutory basis',
      basisHint: 'The legal ground for what was kept or refused, e.g. a retention duty or Art. 12(5) GDPR.',
      submit: 'Close request',
      errorOutcome: 'Please choose what was done.',
      errorBasis: 'A refusal or a partial answer must name its statutory basis.',
      conflict: 'The request could not be closed this way. A legal hold or an open veto window may block erasing the data, or the request changed in the meantime — reload the list and check.',
    },
    record: {
      title: 'Record a request',
      body: 'For a request that reached you by email, post or phone. The one-month deadline runs from the day it arrived, not from today. A recorded request starts as received.',
      choose: 'Choose…',
      kindLabel: 'Type of request',
      channelLabel: 'Arrived via',
      receivedLabel: 'Received on',
      receivedHint: 'The day the request reached the workspace — today at the latest.',
      subjectLabel: 'Person concerned',
      modes: {
        person: 'A person in People',
        candidate: 'An applicant in Recruiting',
        unlinked: 'Not on file',
      },
      personLabel: 'Find the person',
      personHint: 'Search by name or email address.',
      candidateLabel: 'Applicant',
      candidateHint: 'Search by name or email address. Only applicants your Recruiting role may open are found.',
      candidateRefused: 'Your roles in Recruiting do not allow searching applicants. Choose “Not on file” and enter the name the person gave.',
      searchPlaceholder: 'Name or email address',
      picker: {
        searching: 'Searching…',
        typeMore: 'At least {count} characters',
        noMatch: 'Nobody found. If the person is not on file, choose “Not on file”.',
        error: 'The search failed. If the person is not on file, choose “Not on file”.',
        refused: 'Searching applicants is not allowed for your roles.',
        rateLimited: 'Too many searches – back in a moment',
        resultsAnnouncement: 'One match | {count} matches',
        clear: 'Clear selection',
      },
      nameLabel: 'Name given',
      nameHint: 'As the person signed or introduced themselves.',
      emailLabel: 'Email address (optional)',
      detailLabel: 'What is being asked?',
      detailHint: 'Optional. In the person’s own words, as far as possible.',
      reasonLabel: 'Why are you recording this on their behalf?',
      reasonHint: 'For example: letter to HR of 3 May, or a call taken at reception. Recorded with your name.',
      submit: 'Record request',
      errorKind: 'Please choose the type of request.',
      errorChannel: 'Please choose how the request arrived.',
      errorReceived: 'Please choose the day the request arrived.',
      errorFuture: 'A request cannot arrive in the future. Choose today or an earlier day.',
      errorPerson: 'Please pick the person from the search, or choose “Not on file”.',
      errorCandidate: 'Please pick the applicant from the search, or choose “Not on file”.',
      errorName: 'Please enter the name the person gave.',
      errorEmail: 'Please enter a valid email address, or leave the field empty.',
      errorReason: 'Please say why you are recording the request on their behalf.',
    },
    errors: {
      conflict: 'This step is no longer possible — the request changed in the meantime. Reload the list.',
      notLinked: 'This request is not matched to a record, so there is nobody to hold or restrict.',
    },
    progress: {
      title: 'Erasure by module',
      show: 'Show erasure progress',
      hide: 'Hide erasure progress',
      loading: 'Loading the erasure record…',
      loadError: 'The erasure record could not be loaded.',
      unlinked: 'This request was not matched to a record, so no module erased anything for it.',
      lastExecuted: 'recorded on {date}',
      states: {
        done: 'Erased',
        heldNothing: 'Held nothing',
        pending: 'Not recorded yet',
        deferred: 'Deferred — module deactivated',
      },
      modules: {
        people: 'People',
        ats: 'Applications',
        time: 'Time',
        apprentice: 'Apprenticeship',
        payroll: 'Payroll',
        privacy: 'Data protection record',
      },
    },
    postpone: {
      title: 'Pause the reminders',
      body: 'Stops the reminder mails until the chosen day. The deadline itself does not move — to move it, extend the request.',
      untilLabel: 'Pause until',
      untilHint: 'At the latest the answer due date, {date}.',
      reasonLabel: 'Reason',
      reasonHint: 'Recorded with the request, so the pause can be explained later.',
      submit: 'Pause reminders',
      errorUntil: 'Please choose the day the reminders resume.',
      errorRange: 'Choose a day from tomorrow up to the answer due date.',
      errorReason: 'Please give the reason for the pause.',
    },
    veto: {
      title: 'Open the veto window?',
      body: 'For the next 72 hours the erasure is held back: the request cannot be closed as erased. Use the time to check whether a legal hold or a refusal is needed. The window cannot be closed early.',
      confirm: 'Open veto window',
    },
    holds: {
      title: 'Legal holds',
      intro: 'A legal hold stops data from being erased — by a request, the retention rules or the deletion of the workspace — until it is released. Every hold must be confirmed again every six months.',
      placeTenant: 'Hold the whole workspace',
      empty: 'No legal hold is active.',
      lapsedNotice: 'Holds past their confirmation date: {count}. They still block erasure — confirm or release them.',
      scopeTenant: 'Whole workspace',
      scopeSubject: 'One person',
      lapsed: 'Confirmation overdue',
      placedAt: 'Placed',
      attestationDue: 'Confirm by',
      basis: 'Legal basis',
      attest: 'Still needed',
      attestConfirm: {
        title: 'Keep this hold?',
        body: 'You confirm that the reason for the hold still applies. It stays in place for another six months.',
        confirm: 'Keep hold',
      },
      release: {
        open: 'Release',
        title: 'Release the legal hold',
        warning: 'From now on the data this hold protected may be erased — by pending requests, the retention rules or the deletion of the workspace. This cannot be undone.',
        reasonLabel: 'Why is the hold no longer needed?',
        reasonHint: 'Recorded with your name.',
        submit: 'Release hold',
        errorReason: 'Please give the reason for the release.',
      },
      place: {
        titleTenant: 'Hold the whole workspace',
        titleSubject: 'Hold this person',
        bodyTenant: 'No data of this workspace may be erased while the hold stands — not by requests, the retention rules or the deletion of the workspace.',
        bodySubject: 'No data about the person this request names may be erased while the hold stands. The request itself must still be answered in time.',
        reasonLabel: 'Reason',
        reasonHint: 'For example the court case or the authority request that requires the data to be kept.',
        basisLabel: 'Legal basis (optional)',
        basisHint: 'A short reference, for example the statute or the case number.',
        submit: 'Place hold',
        errorReason: 'Please give the reason for the hold.',
      },
      errors: {
        notActive: 'This hold is no longer active — it was changed in the meantime. The list has been reloaded.',
      },
    },
    restrictions: {
      title: 'Restrictions of processing',
      intro: 'A restricted record may be kept but not used (Art. 18 GDPR). Before a restriction is lifted, the person must be told.',
      empty: 'No processing is restricted.',
      originSystem: 'Placed after an erasure',
      originRequest: 'From a request',
      notified: 'Person told on {date}',
      notNotified: 'Person not told yet',
      placedAt: 'Restricted since {date}',
      notifyOpen: 'Tell the person',
      notifyAgain: 'Record a new notice',
      lift: 'Lift restriction',
      liftConfirm: {
        title: 'Lift the restriction?',
        body: 'The record can be used again as before. The person has been told.',
        bodySystem: 'This restriction was placed automatically over what was kept after the person’s data was erased. Lifting it makes these records editable and usable again. Only lift it if keeping them restricted is no longer required.',
        confirm: 'Lift restriction',
      },
      errors: {
        notActive: 'This restriction is no longer active, or the person has not been told yet. The list has been reloaded.',
      },
      place: {
        title: 'Restrict processing',
        body: 'The record of the person this request names is kept but may no longer be used until the restriction is lifted. The restriction is linked to this request.',
        reasonLabel: 'Reason',
        reasonHint: 'For example: the person disputes the accuracy of the data.',
        submit: 'Restrict',
        errorReason: 'Please give the reason for the restriction.',
      },
      notify: {
        title: 'Tell the person',
        body: 'The person must be told before the restriction is lifted (Art. 18(3) GDPR).',
        modeLabel: 'How is the person told?',
        modeSend: 'lumi sends an email',
        modeManual: 'Already told another way',
        manualOnly: 'This restriction is not linked to a request, so lumi has no address. Tell the person yourself and record it here.',
        employerLabel: 'Employer named in the email',
        employerHint: 'The person must be able to see who restricted their data.',
        refLabel: 'Reference',
        refHint: 'How you told them, for example “letter of 3 May” or a file number.',
        dateLabel: 'Told on',
        dateHint: 'Leave empty for today.',
        submitSend: 'Send email',
        submitManual: 'Record notice',
        sendRefused: 'The email could not be sent: the request holds no email address. Tell the person another way and record it.',
        errorEmployer: 'Please name the employer.',
        errorRef: 'Please give a reference for the notice.',
      },
    },
    review: {
      title: 'Review the copy',
      intro: 'This is how the person will read their copy. You decide each flagged entry on its own: release it or withhold it (Art. 15(4) GDPR). Everything else is read-only.',
      back: 'Back to the requests',
      counter: {
        undecided: '{undecided} of {flagged} entries still to decide',
        allDecided: 'All {flagged} flagged entries are decided',
        noneFlagged: 'No entry is flagged — you can release the copy right away',
      },
      modules: {
        people: 'People',
        ats: 'Applications',
        time: 'Time',
        apprentice: 'Apprenticeship',
      },
      moduleState: {
        notInstalled: 'This module is not part of this workspace, so nothing is held there.',
        removed: 'This module was removed from the workspace; nothing about the person is left in it.',
        unavailable: 'This module could not be reached when the copy was assembled — this area is missing from the copy. Set the request to “Wait for the person” and then “Start working on it” again, and lumi assembles the copy afresh — or release it knowingly without this area.',
      },
      moduleEmpty: 'Nothing about the person is stored here.',
      delivered: 'Delivered — lumi is about to close the request',
      self: {
        title: 'This request is about you',
        description: 'Another data protection officer has to review the copy. You can answer the request by hand instead.',
      },
      unverified: 'lumi could not check just now whether this request is about you. Please try again in a moment.',
      sectionMeta: 'Processing: {activity}',
      flagged: 'Flagged',
      flagReason: {
        written: 'Written by someone else about the person, or names other people. Weigh whether the person may read this entry.',
        generic: 'May contain information about other people or come from others. Weigh it under Art. 15(4) GDPR.',
      },
      state: {
        undecided: 'Not decided yet',
        release: 'Will be released',
        withhold: 'Will be withheld',
      },
      decision: {
        group: 'Decision on an entry in “{section}”',
        release: 'Release',
        withhold: 'Withhold',
        changeReason: 'Change reason',
        reasonLabel: 'Reason for the person',
        reasonHint: 'The person reads this reason in their copy. Write it for them, and do not repeat the entry’s content.',
        reasonSave: 'Save as withheld',
        reasonRequired: 'Please give a reason.',
        saveError: 'The decision could not be saved. The entry keeps its previous state.',
      },
      release: {
        action: 'Release the copy',
        blocked: 'Decide the {undecided} open flagged entries first, then you can release the copy.',
        confirmTitle: 'Release the copy?',
        confirmBody: 'lumi now creates the copy without the withheld entries ({withheld}), notifies the person and closes the request.',
        confirmBodyNone: 'lumi now creates the copy, notifies the person and closes the request.',
        confirmLabel: 'Release',
        done: 'Released — lumi is delivering the copy',
      },
      errors: {
        undecided: 'Some flagged entries are still open. The page has been reloaded.',
        expired: 'The copy has expired — lumi creates a new one, and you will see it here again soon',
        closed: 'The request is no longer open. The copy can no longer be released.',
      },
      notFound: {
        title: 'No copy is waiting here',
        description: 'The copy has expired or was already delivered. If the request is still open, lumi creates a new one and you will see it in the list again soon.',
      },
    },
  },
  legalNotices: {
    title: 'Employee privacy & AI notice',
    intro: 'What this workspace tells its own employees about privacy and the use of AI at work.',
    titleLabel: 'Title',
    contentLabel: 'Notice text',
    editMode: 'Edit',
    previewMode: 'Preview',
    followingVersion: 'Following lumi version {version}',
    branchedFromVersion: 'Own text (branched from version {version} on {date})',
    updateAvailable: 'A newer lumi version ({version}) is available.',
    compareVersions: 'Compare',
    hideComparison: 'Hide comparison',
    yourText: 'Your text',
    lumiVersionHeading: 'lumi version {version}',
    adoptVersion: 'Adopt version {version}',
    adoptConfirm: "Replace your text in both languages with lumi's version {version}?",
    adopted: "Adopted lumi's version.",
    saved: 'Saved.',
    noticeRequired: 'The notice cannot be saved empty.',
    lumiVersionUnavailable: "lumi's current version could not be reached — try again shortly.",
    forbidden: 'You do not have access to this notice.',
    supportRefused: 'A support session does not extend to this notice.',
    membershipUnavailable: 'Your access could not be checked right now — try again shortly.',
    loadFailed: 'The notice could not be loaded.',
    compareUnavailable: 'lumi could not be reached, so the comparison and Adopt are unavailable right now.',
    blanksOpenRefusal: 'The text still contains open blanks: {list}. Fill them in above, or replace them with your own words, before saving.',
    unsavedWarning: 'You have not yet saved your changes to {name}.',
    demoDisclaimer: 'THIS IS THE DEMO VERSION FOR A FICTIONAL DEMO COMPANY. NOT A LEGALLY BINDING DOCUMENT.',
    blanks: {
      lead: "lumi's templates need these facts from you. Enter each one once — it is written into every text below, in both languages.",
      saved: 'Saved.',
      complete: 'All required information has been provided.',
      suggested: 'Taken from your company profile – please check it, then save.',
      keys: {
        company_name: { label: 'Company name', hint: 'The legal name of this workspace, as it should appear in the notice.' },
        company_address: { label: 'Company address', hint: 'The postal address employees and applicants can reach you at.' },
        privacy_contact: { label: 'Data protection contact', hint: 'Who to ask about data protection — a name, a role, or an email address.' },
        applicant_retention: { label: 'Applicant data retention period', hint: 'How long unsuccessful applications are kept before deletion.' },
      },
    },
    // The page's two-step checklist (issue 766): the facts form, then the imprint.
    checklist: {
      title: 'Completing your legal notices',
      progress: '{done} of {total} done',
      facts: { title: 'Details for every text', hint: 'Company name, address, data protection contact and more — written into every text below.' },
      imprintAction: 'Enter imprint',
    },
    candidate: {
      heading: 'Candidate notices',
      intro: 'The legal notices this workspace shows candidates on the career portal.',
      unavailable: 'The candidate notices are not available right now.',
      noticeRequired: 'This notice is shown to applicants and cannot be left empty. Replace the text, or adopt lumi\'s version.',
      imprintRequired: 'The imprint cannot be emptied while the portal is live. Replace the text instead of clearing it.',
      privacy: {
        title: 'Tenant-specific privacy notice',
        subtitle: 'This version is displayed on the portal for applicants. Without it the career portal stays offline.',
      },
      aiUsage: {
        title: 'AI-usage notice for applicants',
        subtitle: 'This text is shown to applicants on the career portal.',
      },
      imprint: {
        title: 'Career portal imprint',
        subtitle: 'This text is linked from every page of the portal.',
        hint: 'An imprint is required by law (§ 5 DDG). Your career portal stays offline until one is saved, in any language.',
        fallsBack: 'Your imprint is saved. Languages without an imprint of their own show the {language} version on the portal.',
      },
      history: {
        title: 'What did we show on a given date?',
        hint: 'Look up the exact text this page had live at a moment in the past — for a candidate\'s question or an authority\'s.',
        open: 'Look up',
        hide: 'Close',
        dateLabel: 'Date and time',
        localeLabel: 'Language',
        lookUp: 'Show the text',
        periodClosed: 'Live from {from} until {until}.',
        periodOpen: 'Live from {from} — still the current text.',
        sourceLumi: 'lumi\'s published version {version}, word for word.',
        sourceLumiUnversioned: 'lumi\'s published text.',
        sourceTenant: 'This workspace\'s own text, branched from lumi version {version}.',
        sourceTenantOwn: 'This workspace\'s own text.',
        none: 'The record does not reach back that far.',
        provenanceFollowing: 'What is known: this page is a copy of lumi version {version} and nobody here has written over it.',
        provenanceBranched: 'What is known: this page came from lumi version {version} and was written over on {date}.',
        provenanceUnknown: 'Nothing further is recorded about this page before then.',
        futureRefused: 'This record says what a page showed, not what it will show. Choose a moment that has passed.',
        invalidRefused: 'Choose a date and a language for the lookup.',
        failed: 'The record could not be read just now. Please try again.',
      },
    },
  },
  legalNoticePublic: {
    unavailable: 'This notice is not available right now.',
  },
  dataImportHub: {
    segments: {
      import: 'Import',
      export: 'Export',
      label: 'Import or export',
    },
    umzug: {
      label: 'Your migration',
      from: 'Migration from',
      pick: 'Choose migration',
      files: '{count} file, last {when} | {count} files, last {when}',
      startNew: 'New migration',
      nameIntro: 'A migration groups the files that come from the same system. Give it a name, ideally that of the old system.',
      nameLabel: 'Name of the old system',
      nameHint: 'Lowercase letters, digits, hyphen and underscore; nothing else is kept.',
      namePlaceholder: 'e.g. personio',
      begin: 'Start migration',
      nameReserved: 'Lumi cannot use this name. Please pick another one.',
      together: 'The files of a migration belong together: later files find what earlier ones created. Foundations first, then what builds on them.',
    },
    order: 'Order of the migration',
    entries: {
      orgUnits: {
        title: 'Departments & locations',
        description: 'Departments, teams, locations and cost centres from the old system.',
        reason: 'Departments and locations are the foundation for everything else.',
      },
      persons: {
        title: 'Employees',
        description: 'Everyone who works for you, with master data, department and manager.',
        reason: 'You need the employees for contracts, time and absences.',
      },
      contracts: {
        title: 'Contracts',
        description: 'Employment contracts of the employees.',
        needs: 'needs the employees',
        reason: 'Contracts hang on the employees.',
      },
      ats: {
        title: 'Jobs, candidates & applications',
        description: 'Open and past jobs with their candidates and applications.',
        needs: 'needs the departments & locations',
        reason: 'Jobs refer to your locations.',
      },
      time: {
        title: 'Clients, projects, time & absences',
        description: 'Clients, projects, recorded time and absences.',
        needs: 'needs the employees',
        reason: 'Time and absences belong to the employees.',
      },
    },
    state: {
      next: 'Up next.',
      imported: '{count} imported · {file} · {when}',
      importedMany: '{count} imported · {files} files · last {when}',
      unfinished: 'Started with {file}',
    },
    actions: {
      resume: 'Continue',
      supplement: 'Add more',
      continueHere: 'Continue with this',
    },
    drop: {
      title: 'Drop a file here or choose one',
      hint: 'CSV or Excel; an export from the old system is enough.',
      after: 'After that:',
      savedLast: 'nothing is saved until the last step',
    },
    ai: {
      label: 'Lumi improves the mapping',
      infoLabel: 'What goes to Lumi',
      infoSent: 'Only the column names and at most 20 sample values per column go to the AI. For columns whose name points to sensitive data, no values go along.',
      infoProposal: 'The result is only a proposal: you check and confirm every mapping yourself.',
      infoQuota: 'The improvement counts against your AI quota.',
      quotaUsedUp: 'No AI quota left until {date}.',
      quotaUsedUpNoDate: 'No AI quota left in this period.',
      suspended: 'Lumi is switched off in this workspace.',
      raise: 'Raise quota',
      withoutQuota: 'By rules',
    },
    lock: {
      body: 'In {module} you lack the permission {permission}. It belongs to the role {role}; suite admin alone is not enough. It is granted in People under {where} by someone with the role {granters}.',
      permissionImport: 'Import data',
      permissionExport: 'Export data',
      accessPage: 'Rights & assignments',
      adminRole: '{module} admin',
      granters: 'owner or suite admin',
      askSomeone: 'Please ask someone with the role {role}.',
      grantSelf: 'Give it to myself (People → Rights & assignments)',
      mayManage: 'You may manage access.',
    },
    lists: {
      imports: 'Imports in this migration ({count})',
      others: 'Other migrations ({count})',
      show: 'Show',
      hide: 'Hide',
      noImports: 'Nothing has been imported in this migration yet.',
      noOthers: 'No other migrations.',
      retention: 'Files and reports are deleted 30 days after the import; the data taken over stays.',
      report: 'Report',
      delete: 'Delete',
      switchTo: 'Switch',
      you: 'you',
      columns: {
        file: 'File',
        entry: 'Entry',
        result: 'Result',
        when: 'When',
        by: 'By',
      },
    },
    result: {
      done: 'Imported',
      review: 'To review',
      questions: 'Questions open',
      reading: 'Being read',
      checking: 'Being checked',
      committing: 'Being applied',
      cancelled: 'Cancelled',
      failed: 'Failed',
      created: '{count} new',
      updated: '{count} updated',
      unchanged: 'nothing changed',
      toClarify: '{count} to clarify',
    },
    done: {
      headline: 'Arrived in {module}: {count} {things}.',
      headlineRecords: 'Arrived in {module}: {count} records.',
      numbers: '{created} created · {updated} updated',
      notImported: '{count} row not imported | {count} rows not imported',
      leftOut: '{count} row left out by you | {count} rows left out by you',
      downloadList: 'Download the list',
      laterHint: ' (it will not be available later)',
      skipped: '{count} row skipped unchanged | {count} rows skipped unchanged',
      soFar: 'Where you stand',
      rowsIn: '{count} imported',
      asNext: 'Up next: {reason}',
      whatNext: 'What happens next?',
      nextTitle: 'Continue with: {entry}',
      nextAction: 'Next file',
      orgChartTitle: 'Look at the org chart',
      orgChartBody: 'Check that departments and managers arrived the way you know them.',
      orgChartBuildTitle: 'Build the org chart',
      orgChartBuildBody: 'Everyone leads themselves for now. You set up who reports to whom in People.',
      accessTitle: 'Give access',
      accessBody: 'The employees are there, but nobody can sign in yet. You give access in People.',
      openPeople: 'Open in People',
    },
    export: {
      title: 'Export',
      intro: 'Take your data out of a module. Every export runs under your permission in that module.',
      covers: {
        people: 'Employees, departments, contracts',
        ats: 'Jobs, candidates, applications',
        time: 'Clients, projects, time, absences',
      },
      locked: 'In {module} you lack the permission {permission}. It belongs to the role {role}.',
      last: 'Last exported {when} · {count} entries',
      none: 'Not exported yet.',
      past: 'Past exports',
      columns: {
        module: 'Module',
        by: 'By',
      },
      standard: 'Standard export',
      expired: 'Expired',
      archive: 'You find the {whole} under {link}.',
      archiveWhole: 'whole data set of the workspace',
    },
    run: {
      breadcrumb: 'Migration from {umzug} › {entry} · {file}',
    },
    nav: 'Data import',
    title: 'Data import & export',
    locked: 'No permission in this module',
    plan: {
      failed: 'The additional data could not be created.',
      companionErrors: 'Errors occurred while creating the additional data. Please check it in the module itself.',
      projectionTimeout: 'The additional data has not reached the target module yet. Try again later.',
    },
    createField: {
      title: 'Create new custom field',
      intro: 'Create a custom field for the column “{column}”. It becomes available everywhere in the ATS.',
      typeHint: 'Choice fields (select/multi-select) are created in the field management.',
      sensitiveNotImportable: 'Special-category (Art. 9) fields are created but are deliberately not an import target.',
      submit: 'Create field',
      label: 'Label',
      keyLabel: 'Field key (Key)',
      keyHint: 'Lowercase letters, numbers and underscores only',
      typeLabel: 'Type',
      types: {
        text: 'Text',
        textarea: 'Textarea',
        number: 'Number',
        date: 'Date',
        checkbox: 'Checkbox',
      },
      sensitivityLabel: 'What kind of data does this field hold?',
      sensitivityUnanswered: 'Please choose',
      sensitivityNone: 'No special category',
      sensitivityHint: 'Special-category data (Art. 9 GDPR) may not drive automated decisions about an application.',
      sensitivityCategories: {
        racialEthnic: 'Racial or ethnic origin',
        politicalOpinion: 'Political opinions',
        religiousPhilosophical: 'Religious or philosophical beliefs',
        tradeUnion: 'Trade union membership',
        genetic: 'Genetic data',
        biometricId: 'Biometric identification',
        health: 'Health data',
        sexLifeOrientation: 'Sex life or sexual orientation',
      },
      sensitivityRequired: 'Please state what kind of data this field holds.',
    },
    entities: {
      person: 'Employees',
      orgUnit: 'Org units',
      employmentContract: 'Contracts',
      job: 'Jobs',
      candidate: 'Candidates',
      application: 'Applications',
      client: 'Clients',
      project: 'Projects',
      timeEntry: 'Time entries',
      leaveRequest: 'Leave requests',
    },
    // People's enum values in the words People uses for them (people-ui's own
    // labels): the import wizard names allowed values with these.
    peopleValues: {
      gender: {
        male: 'Male',
        female: 'Female',
        diverse: 'Diverse',
        undisclosed: 'Not specified',
      },
      maritalStatus: {
        single: 'Single',
        married: 'Married',
        divorced: 'Divorced',
        widowed: 'Widowed',
        registered_partnership: 'Registered partnership',
        separated: 'Separated',
      },
      contractType: {
        permanent: 'permanent',
        fixed_term: 'fixed-term',
        mini_job: 'mini-job',
        midi_job: 'midi-job (transition range)',
        working_student: 'working student',
        apprenticeship: 'apprenticeship',
        internship: 'internship',
        freelance: 'freelance / contractor',
        managing_director: 'managing director / board member',
        other: 'other',
      },
      workTimeArrangement: {
        full_time: 'full-time',
        part_time: 'part-time',
        marginal: 'marginal (mini-job)',
        variable: 'variable / flexible',
      },
      status: {
        draft: 'Draft',
        active: 'Active',
        on_hold: 'On hold',
        terminated: 'Terminated',
        inactive: 'Inactive',
      },
      type: {
        department: 'Department',
        location: 'Location',
        team: 'Team',
        cost_center: 'Cost center',
      },
    },
    // People's import fields in People's own words (people-ui's labels), per
    // entity: the wizard's row errors name a field with these, never by its key.
    peopleFields: {
      orgUnit: {
        externalId: 'External ID',
        type: 'Type',
        name: 'Name',
        code: 'Code',
        description: 'Description',
        parentExternalId: 'Parent unit',
        parentCode: 'Parent unit (Code)',
        addressLine1: 'Address',
        postalCode: 'Postal code',
        city: 'City',
        country: 'Country',
        active: 'Active',
      },
      person: {
        externalId: 'External ID',
        employeeNumber: 'Employee number',
        firstName: 'First name',
        lastName: 'Last name',
        salutation: 'Salutation',
        gender: 'Gender',
        email: 'Email',
        phone: 'Phone',
        birthDate: 'Date of birth',
        nationality: 'Nationality',
        maritalStatus: 'Marital status',
        addressLine1: 'Address',
        addressLine2: 'Address line 2',
        postalCode: 'Postal code',
        city: 'City',
        country: 'Country',
        workCountry: 'Work location (country)',
        primaryLocale: 'Language',
        status: 'Status',
        primaryOrgUnitExternalId: 'Department / unit',
        primaryOrgUnitCode: 'Department / unit (Code)',
        supervisorExternalId: 'Functional manager',
      },
      employmentContract: {
        externalId: 'External ID',
        personExternalId: 'Person',
        contractReference: 'Contract number',
        status: 'Contract status',
        startDate: 'Start date',
        endDate: 'End date',
        probationEndDate: 'Probation end',
        contractType: 'Contract type',
        position: 'Position / role',
        primaryOrgUnitExternalId: 'Department / unit',
        supervisorExternalId: 'Manager',
        workTimeArrangement: 'Working time',
        weeklyHours: 'Weekly hours',
        vacationDaysPerYear: 'Annual leave (days/year)',
        collectiveAgreementCode: 'Collective agreement',
      },
    },
    // The ATS import's fields in the ATS UI's own words, per entity (the guided import,
    // issue 1063): the mapping step and the row errors name a field with these, never
    // by its key. Every key of ats-api's import-descriptor.ts has one in every locale.
    atsFields: {
      job: {
        externalId: 'External ID',
        title: 'Job title',
        departmentName: 'Department',
        locationName: 'Location',
        state: 'Status',
        headcount: 'Positions to fill',
        employmentType: 'Employment type',
        requiredSkills: 'Required skills',
      },
      candidate: {
        externalId: 'External ID',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone',
        skills: 'Skills',
        availableFrom: 'Available from',
        noticePeriodDays: 'Notice period (days)',
        remotePreference: 'Work location preference',
        salaryExpectation: 'Salary expectation (per year)',
        seniority: 'Seniority',
        poolTags: 'Talent pool tags',
      },
      application: {
        externalId: 'External ID',
        candidateExternalId: 'Candidate',
        jobExternalId: 'Job',
        state: 'Stage',
        appliedAt: 'Applied on',
        note: 'Note',
      },
    },
    // The ATS descriptor's fixed enum values in the ATS UI's words (the workflow states and
    // the department/location names are the workspace's own and need none).
    atsValues: {
      remotePreference: {
        onsite: 'On site',
        hybrid: 'Hybrid',
        remote: 'Remote',
      },
      seniority: {
        junior: 'Junior',
        mid: 'Mid-level',
        senior: 'Senior',
        lead: 'Lead',
      },
    },
    // Time's import fields in Time's own words, per entity (see atsFields).
    timeFields: {
      client: {
        externalId: 'External ID',
        name: 'Name',
        isInternal: 'Internal',
      },
      project: {
        externalId: 'External ID',
        name: 'Project name',
        clientExternalId: 'Client',
        externalRef: 'Project reference',
        description: 'Description',
        status: 'Status',
        isBillable: 'Billable',
        hourlyRate: 'Hourly rate',
        budgetHours: 'Hours budget',
      },
      timeEntry: {
        externalId: 'External ID',
        userEmail: 'Email of the person',
        personnelNumber: 'Employee number',
        date: 'Date',
        startTime: 'Start time',
        endTime: 'End time',
        durationMinutes: 'Duration (minutes)',
        bookingType: 'Booking type',
        projectExternalId: 'Project',
        activity: 'Activity',
        note: 'Note',
        status: 'Status',
      },
      leaveRequest: {
        externalId: 'External ID',
        userEmail: 'Email of the person',
        personnelNumber: 'Employee number',
        leaveType: 'Leave type',
        startDate: 'From',
        endDate: 'To',
        status: 'Status',
        note: 'Note',
      },
    },
    // Time's enum values. `status` is shared by projects, time entries and absences, so it
    // carries the union of their values (the value maps are keyed by field, per module).
    timeValues: {
      status: {
        active: 'Active',
        closed: 'Closed',
        paused: 'Paused',
        completed: 'Completed',
        archived: 'Archived',
        draft: 'Draft',
        submitted: 'Submitted',
        approved: 'Approved',
        pending: 'Pending',
        rejected: 'Rejected',
      },
      leaveType: {
        vacation: 'Vacation',
        special: 'Special leave',
        parental: 'Parental leave',
        unpaid: 'Unpaid leave',
        training: 'Training',
      },
    },
  },
  // ── Tenant administration (#432): the five pages that used to be their own
  //     app. Merged verbatim from its catalogue, so the strings are unchanged.
  audit: {
    title: 'Audit Log', subtitle: 'Who did what in your workspace.',
    noAccess: 'No access', noAccessHint: 'You need the audit log permission to view this page.',
    empty: 'No audit events yet', emptyHint: 'Actions in your workspace will appear here.',
    loading: 'Loading…', loadMore: 'Load more',
    filters: { from: 'From', to: 'To', action: 'Action', category: 'Category', actor: 'Actor', outcome: 'Outcome', search: 'Search', searchHint: 'Subject or actor…', apply: 'Apply', reset: 'Reset', all: 'All', actorType: 'Actor type' },
    columns: { time: 'Time', actor: 'Actor', action: 'Action', subject: 'Subject', outcome: 'Outcome' },
    outcome: { success: 'Success', denied: 'Denied', failure: 'Failure' },
    actorType: { user: 'User', system_admin: 'System Admin', support: 'Support', service: 'Service', system: 'System' },
    categories: { auth: 'Auth', access: 'Access', data: 'Data', admin: 'Admin', lifecycle: 'Lifecycle', export: 'Export', security: 'Security' },
    export: { csv: 'Export CSV', ndjson: 'Export NDJSON' },
    detail: { roles: 'Roles', action: 'Action', actorId: 'Actor ID', onBehalfOf: 'On behalf of', ip: 'IP address', source: 'Source', correlationId: 'Correlation ID', changes: 'Changes', before: 'Before', after: 'After', details: 'Details' },
    actions: { login: { succeeded: 'Login succeeded', failed: 'Login failed', attempts_flagged: 'Repeated failed sign-in attempts', locked: 'Account locked' }, sso: { login_succeeded: 'SSO login succeeded', connection_changed: 'SSO connection changed' }, password: { reset_requested: 'Password reset requested', reset_completed: 'Password reset completed' }, refresh_token: { revoked: 'Session revoked' }, user: { signed_up: 'User signed up', unlocked: 'Account unlocked', disabled: 'Account disabled', enabled: 'Account enabled', closed: 'Account closed' }, passkey: { revoked: 'Passkeys removed by an admin' }, role: { changed: 'Role changed' }, module_access: { changed: 'Module access changed' }, access: { ownership_transferred: 'Ownership transferred', member_removed: 'Member removed', suite_roles_changed: 'Suite roles changed' }, tenant: { created: 'Tenant created', lifecycle: 'Tenant lifecycle change', exported: 'Tenant exported' }, module: { activated: 'Module activated', deactivated: 'Module deactivated' }, plan: { changed: 'Plan changed' }, tenant_ai_settings: { changed: 'AI settings changed' }, tenant_role_editing: { changed: 'Role editing changed' }, settings: { changed: 'Settings changed' }, test_data: { seeded: 'Test data seeded', reset: 'Test data reset' }, onboarding: { setup_chosen: 'Setup chosen', demo_started: 'Demo mode started', demo_seeded: 'Demo data seeded', demo_seed_failed: 'Demo seeding failed', demo_wipe_started: 'Demo wipe started', demo_wiped: 'Demo data wiped', demo_wipe_failed: 'Demo wipe failed', setup_finished: 'Setup finished', demo_denied: 'Demo mode denied', module_topup: 'Demo data added for new module', demo_reset_started: 'Demo reset started', demo_tier_switch_started: 'Switch to the full demo started', demo_rebuilt: 'Demo rebuilt' }, company_profile: { created: 'Company profile created', updated: 'Company profile updated' }, org_scenario: { activated: 'Org scenario activated' }, people_data: { wiped: 'People data wiped' }, ats_data: { wiped: 'Recruiting data wiped' }, time_data: { wiped: 'Time tracking data wiped' }, apprentice_data: { wiped: 'Apprenticeship data wiped' }, audit_log: { exported: 'Audit log exported' }, billing_charge: { paid: 'Payment charged', failed: 'Payment failed', refunded: 'Payment refunded', chargeback: 'Chargeback received' }, invoice: { issued: 'Invoice issued' }, mandate: { created: 'Card added', replaced: 'Card replaced' }, promo_code: { redeemed: 'Promo code redeemed' }, billing_account: { converted: 'Trial converted to paid', delinquent: 'Account delinquent', purged: 'Billing data purged' }, dunning_case: { opened: 'Payment recovery started' }, enforcement: { opened: 'Enforcement started', advanced: 'Enforcement escalated', cleared: 'Enforcement cleared' }, billing_exempt: { changed: 'Billing exemption changed' }, billing_data: { wiped: 'Billing data wiped', generated: 'Test billing data generated' }, checkout: { completed: 'Checkout completed', denied: 'Checkout denied' }, job: { created: 'Job created', updated: 'Job updated', submitted: 'Job submitted', archived: 'Job archived', deleted: 'Job deleted' }, application: { created: 'Application received', updated: 'Application updated', transitioned: 'Application status changed', withdrawn: 'Application withdrawn', transferred: 'Application moved to another job', transfer_declined: 'Application move declined', rejected: 'Application rejected', deleted: 'Application deleted', emailed: 'Application email sent' }, interview: { proposed: 'Interview proposed', confirmed: 'Interview confirmed', cancelled: 'Interview cancelled' }, candidate: { anonymized: 'Candidate anonymized', emailed: 'Candidate email sent', identity_changed: 'Candidate details changed', identity_split: 'Candidate separated onto its own record', identity_merged: 'Application moved to the candidate holding its address' }, candidate_dossier: { accessed: 'Candidate viewed their own data' }, workflow: { deleted: 'Workflow deleted', definition: { published: 'Workflow published' } }, task: { blocking_overridden: 'Blocking task overridden', auto_cancelled: 'Task automatically cancelled' }, support_access: { requested: 'Support access requested', approved: 'Support access approved', denied: 'Support access denied', revoked: 'Support access ended', expired: 'Support access expired', request_expired: 'Support access request expired' }, ai_action: { executed: 'AI action executed' }, assistant_run: { proposed: 'lumi proposed an action', approved: 'lumi action approved', refused: 'lumi action refused', failed: 'lumi action failed' }, assistant_turn: { fanned_out: 'lumi request spanned several modules' }, payroll_export_run: { created: 'Payroll export started', completed: 'Payroll export completed', failed: 'Payroll export failed', downloaded: 'Payroll export downloaded' }, payroll_period: { closed: 'Payroll month closed', reopened: 'Payroll month reopened' }, payroll_connection: { updated: 'Payroll connection changed' }, payroll_master_data: { exported: 'Payroll master data exported' }, wage_type: { created: 'Wage type created', updated: 'Wage type updated', deleted: 'Wage type deleted', starter_catalog_applied: 'Standard wage types added' }, wage_assignment: { created: 'Pay component added', updated: 'Pay component changed', deleted: 'Pay component removed' }, one_time_payment: { created: 'One-time payment added', updated: 'One-time payment changed', deleted: 'One-time payment removed' }, payslip: { filed: 'Payslips filed' } },
  },
  sso: {
    title: 'Single Sign-On',
    subtitle: 'Connect your identity provider (OIDC) so your team signs in through it.',
    issuerUrl: 'Issuer URL (OIDC discovery)',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    clientSecretKeep: 'Leave empty to keep the stored secret',
    label: 'Display name',
    allowedDomains: 'Allowed email domains (comma-separated)',
    jitEnabled: 'Create users automatically on first sign-in (JIT)',
    jitDefaults: 'Default module access for new users',
    jitModulePlaceholder: 'Choose a module',
    jitRoles: 'Roles',
    jitCatalogUnreadable: 'The roles of this module could not be read. The stored roles stay unchanged.',
    jitUnknownRole: '{key} (unknown)',
    addJit: 'Add module',
    remove: 'Remove',
    redirectUri: 'Redirect URI (register this in your IdP)',
    copy: 'Copy',
    test: 'Test connection',
    testOk: 'Discovery succeeded.',
    testFailed: 'Test failed',
    save: 'Save',
    saved: 'Saved.',
    delete: 'Remove connection',
    deleteConfirm: 'Really remove this SSO connection?',
    enforce: 'Require sign-in via SSO only',
    enforceHint: 'Password sign-in is disabled for everyone except system admins and the workspace owner.',
    noConnection: 'No SSO connection configured yet.',
    deleted: 'Connection removed.',
    enforceEnabled: 'Sign-in via SSO is now required.',
    enforceDisabled: 'Password sign-in is available again.',
    jitNeedsVerifiedDomain: 'Users are only created automatically for addresses on a verified domain.',
    domainsTitle: 'Email domains',
    domainsIntro: 'A domain counts for SSO sign-in only once you have proven it is yours: publish this TXT record in the domain’s DNS, then check it. Accounts already linked keep signing in meanwhile.',
    domainStatus: { pending: 'Not verified', verified: 'Verified', legacy: 'Verify soon' },
    domainLegacyUntil: 'Verify by {date}',
    domainRecordName: 'Record name (TXT)',
    domainRecordValue: 'Record value',
    domainVerify: 'Check record',
    domainVerified: '{domain} is verified.',
    domainError: { record_not_found: 'No TXT record found under this name yet. DNS changes can take a while to appear.', value_mismatch: 'A TXT record exists, but its value does not match. Copy the value exactly.', dns_error: 'The DNS lookup failed. Please try again in a few minutes.' },
  },
  users: {
    // What survives /admin/users (retired, ADR 0088 §8.2): the module and suite
    // role names other admin surfaces still render.
    suiteSuiteAdmin: 'Admin',
    suiteBillingManager: 'Billing manager',
    suiteSupportAgent: 'Support agent',
    suiteDataProtection: 'Data protection officer',
    moduleNames: {
      ats: 'Recruiting',
      time_tracking: 'Time',
      people: 'People',
      apprenticeship: 'Ausbildung',
    },
  },
  // The consent page of an agent connection (ADR 0103 rule 10, PDR 0035).
  connectAgent: {
    title: 'Connect an AI agent',
    heading: 'An AI agent wants to act as you',
    intro: 'An AI client that calls itself “{client}” asks to work in lumi as you, in the workspace {workspace}.',
    fields: {
      client: 'Calls itself',
      redirectHost: 'Sends you back to',
      website: 'Website it names',
      workspace: 'In the workspace',
    },
    scopesHeading: 'It asks to:',
    scope: {
      read: 'Read what you can see in lumi',
      act: 'Propose and carry out actions as you — every action needs your confirmation',
      ask: 'Ask lumi questions',
    },
    meaning: {
      heading: 'What allowing means',
      asYou: 'It acts as you, with exactly your permissions in this workspace — never more.',
      egress: 'Whatever it reads in lumi goes to the provider behind “{client}”, outside lumi.',
      revocable: 'You can end the connection at any time on your account page.',
    },
    standing: {
      label: 'Go ahead with actions on my own records without asking each time',
      hint: 'Never covers other people’s records or adverse decisions — those always ask. You can change this at any time on your account page.',
    },
    rolePins: {
      title: 'Role per module',
      hint: 'Where you hold more than one role, choose the one the agent works in. It can never do more than that role allows.',
      default: 'Module default ({rule})',
      defaultRule: {
        first_assigned: 'your first assigned role',
        first_by_key: 'your first role by its key',
        all_roles: 'all your roles together',
      },
      modules: {
        ats: 'Recruiting',
        time: 'Time',
        people: 'People',
      },
      notHeld: 'You no longer hold the role you picked in that module. Please choose again.',
    },
    disabled: {
      title: 'External AI agents are switched off in this workspace',
      body: 'An owner or admin can switch them on under Administration → External AI agents. Until then you can only deny this request.',
    },
    actions: {
      allow: 'Allow',
      deny: 'Deny',
    },
    returning: 'Returning you to {client}…',
    invalid: {
      title: 'This sign-in request is no longer valid',
      hint: 'Start the connection again from your AI client.',
    },
  },
  // Administration › External AI agents (ADR 0103 rule 12, PDR 0035 rule 2).
  externalAgents: {
    title: 'External AI agents',
    cardTitle: 'External AI agents',
    noAccess: 'No access',
    noAccessHint: 'Only the workspace owner and suite admins can change this setting.',
    intro: 'With this switched on, members can connect their own AI client — Claude Code, OpenClaw, … — and let it act as them in this workspace.',
    points: {
      asMember: 'An agent works with exactly the permissions of the member who connected it — never more.',
      confirmed: 'Every action it proposes is confirmed by that member before it happens.',
      adverse: 'Adverse decisions about other people always need the lumi panel; an agent can never confirm them.',
    },
    egress: {
      title: 'Where members’ data goes',
      body: 'Whatever an agent reads flows to the AI client the member connects — a recipient outside lumi’s processor chain, chosen by your workspace and the member.',
      worksCouncil: 'Whether members may use such clients is your decision as the employer — consider involving your works council.',
    },
    switchLabel: 'Allow members to connect external AI agents',
    saving: 'Saving…',
    activeConnections: 'No connection is active. | 1 connection is active. | {n} connections are active.',
    offHint: 'Switching off ends every connection in this workspace at once.',
    roleNote: 'Only the workspace owner or a suite admin can change this.',
    savedOn: 'External AI agents are on. Members can now connect their AI client.',
    savedOff: 'External AI agents are off. | External AI agents are off. 1 connection ended. | External AI agents are off. {n} connections ended.',
    disableConfirm: {
      title: 'Switch off external AI agents?',
      body: 'No connection is active right now; members will no longer be able to connect. | This ends 1 connection at once. | This ends {n} connections at once.',
      confirm: 'Switch off',
    },
  },
  supportAccess: {
    revokeConfirm: 'Support access ends immediately. The support team loses its access to this workspace.',
    title: 'Support Access',
    subtitle: 'Temporary, consented access to your workspace for our support team.',
    noAccess: 'No access',
    noAccessHint: 'Only the workspace owner and suite admins can review support access requests.',
    empty: 'No support access requested',
    emptyHint: 'When our support team needs access to your workspace to work a ticket, the request appears here.',
    pendingTitle: 'Access request pending',
    pendingIntro: '{supporter} is asking for {days} days of access to this workspace.',
    activeTitle: 'Support access is active',
    activeIntro: '{supporter} can currently work inside this workspace.',
    historyTitle: 'History',
    revokeHint: 'Ending access takes effect within a minute.',
    expiringNow: 'Expiring now',
    remainingDays: '{days} d {hours} h left',
    remainingHours: '{hours} h {minutes} min left',
    remainingMinutes: '{minutes} min left',
    fields: {
      requestedAt: 'Requested',
      ticket: 'Support ticket',
      duration: 'Duration',
      durationValue: '{days} days',
      reason: 'Reason given',
      approvedBy: 'Approved by',
      expiresAt: 'Expires',
      remaining: 'Time left',
      grantedModules: 'Granted roles',
    },
    consent: { allRoles: 'The supporter can switch into every role each of these modules offers — they see what your people see, not only the admin view.', modulesHeading: 'This grant covers:', via: 'via {module}',
      heading: 'What you are approving',
      data: 'Support can access this workspace\'s data across all active modules, including personal data of employees and candidates.',
      audited: 'Every action they take is recorded in your audit log and marked as a support action.',
      duration: 'Access ends automatically after {days} days.',
      revocable: 'You can end it at any time; it takes effect within a minute.',
    },
    actions: { approve: 'Approve access', deny: 'Deny', revoke: 'End access now' },
    status: {
      requested: 'Pending',
      active: 'Active',
      denied: 'Denied',
      revoked: 'Ended',
      expired: 'Expired',
      request_expired: 'Request expired',
    },
    columns: { supporter: 'Supporter', requestedAt: 'Requested', status: 'Status', endedAt: 'Ended' },
    success: { approved: 'Support access approved.', denied: 'Request denied.', revoked: 'Support access ended.' },
    errors: {
      consentChanged: 'The consent text has changed. Please read it again before approving.',
      selfApproval: 'The requesting account cannot approve its own request.',
    },
  },
  // Administration › Statutory rules (ADR 0091): the statutory rule catalogue
  // and the workspace's agreements and deviations, moved here from Time's
  // settings with the page.
  statutory: {
    title: 'Statutory rules & agreements',
    intro: 'The statutory rules apply automatically and are kept up to date centrally. Here you only record where your company deviates from them – every deviation needs a basis, such as a collective agreement or a works agreement.',
    disclaimer: 'Orientation values from statute – not legal advice. Collective and works agreements may be stricter.',
    // "Checked, fits us" (issue 713): the one-time confirmation of the statutory defaults.
    confirmation: {
      unchecked: {
        title: 'Not checked yet',
        // The short sentence for someone who may read the page but not confirm (a support grant): no picker, no button.
        body: 'lumi starts with the statutory default rules. If they fit you, confirm that. If a collective agreement or a works agreement applies to you, add a deviation below.',
        confirm: 'Checked, fits us',
        // The mode picker (issue 780) is shown to someone who can confirm; the tile titles are the settings card's own (statutory.workspace.modeFlexible/modeStrict).
        lead: 'lumi starts with the statutory default rules – maximum working time, breaks, rest periods. Decide what happens when a booking breaks one, then confirm that the rules fit your company.',
        standard: 'Default',
        later: 'You can change both at any time later.',
        agreements: 'Does a collective agreement or a works agreement apply to you under which a rule differs or does not apply at all? Then add a {basis} below – the warnings follow it. There is no mode without warnings.',
        agreementsBasis: 'deviation with its basis',
        flexible: {
          personLabel: 'The person', personText: 'sees on saving which rule is broken and by how much – the booking stays.',
          managerLabel: 'The manager', managerText: 'sees the week’s violations at approval and decides together with the person.',
          recordLabel: 'The record', recordText: 'keeps every violation – for you, not against you.',
        },
        strict: {
          personLabel: 'The person', personText: 'cannot save a booking that breaks an enforced rule – and sees why. A missing break is saved and shown.',
          managerLabel: 'The manager', managerText: 'sees at approval only what still arises: missing breaks and automatic closes.',
          fitLabel: 'Fits when', fitText: 'exceptions must not occur at your company – for example with minors.',
        },
      },
      // After a save that is not a review, while unconfirmed (issue 990): the next step, in place.
      afterSave: { body: 'Saved. That does not confirm the working-time rules yet – confirm now that they fit you.', confirm: 'Confirm now' },
      confirmed: { title: 'Checked', body: 'Confirmed on {date} by {name}: The statutory rules fit us.', bodyNoName: 'Confirmed on {date}: The statutory rules fit us.' },
      ownDeviation: { title: 'Checked', body: 'Your workspace has its own deviations, so the rules have been looked at.' },
      error: 'The confirmation could not be saved. Please try again.',
    },
    noJurisdictions: 'No jurisdiction is set yet: there are no people with a work location and no default jurisdiction. Choose a default below and the statutory rules will appear here.',
    jurisdictionEu: 'EU directive',
    jurisdictionDefault: '{name} (default)',
    // Who the number is (issue 990): active employees in Time judged by this law —
    // on a tab that is not the default, only those whose work location is there;
    // on the default's tab, also everyone who falls back to it.
    headcountLine: 'Jurisdiction {country}: these rules currently judge nobody. | Jurisdiction {country}: these rules judge the working time of 1 person in Time who works here. | Jurisdiction {country}: these rules judge the working time of {count} people – every active employee in Time who works here.',
    headcountLineDefault: 'Jurisdiction {country}: these rules currently judge nobody. | Jurisdiction {country}: these rules judge the working time of 1 person in Time who works here or falls under the default. | Jurisdiction {country}: these rules judge the working time of {count} people – every active employee in Time who works here or falls under the default.',
    fallbackLine: 'People without a work location, or with one the catalogue does not cover: {count} – they are judged by {country}.',
    createModels: 'Add this country’s work-time models',
    domains: {
      workingTime: 'Working time',
      pay: 'Time windows for premiums',
      overtime: 'Overtime',
      leave: 'Leave',
    },
    rules: {
      maxDailyHours: 'Maximum daily working time',
      maxWeeklyHours: 'Maximum weekly working time',
      breaks: 'Breaks',
      minDailyRest: 'Daily rest period',
      nightWorkBan: 'Night-work ban',
      minWeeklyRest: 'Weekly rest period',
      avgWeeklyHours: 'Average weekly working time',
      normalDailyHours: 'Normal daily working time',
      normalWeeklyHours: 'Normal weekly working time',
      nightWindow: 'Night time for premiums',
      nightCoreWindow: 'Core night time for higher premiums',
      overtimeThreshold: 'Overtime starts at (hours per week)',
      overtimeSurcharge: 'Overtime premiums',
      minLeaveDays: 'Minimum annual leave',
    },
    kinds: {
      collectiveAgreement: 'Collective agreement',
      worksAgreement: 'Works agreement',
      authorityPermit: 'Permit from the authority',
      companyPolicy: 'Company policy',
    },
    value: {
      hoursUnit: 'h',
      days: '{days} days',
      breakStage: '{minutes} min beyond {hours} h',
      weeklyAverage: '{hours} h on average over {weeks} weeks',
      nightWorkBan: 'no work between {start} and {end}',
      tierFirst: '{percent} % from the first overtime hour',
      tierAfter: '{percent} % after {hours} overtime hours',
      none: 'No rule',
      switchedOff: 'Rule does not apply (switched off)',
    },
    status: {
      statutory: 'Statutory',
      noStatutoryRule: 'No statutory rule',
      infoOnly: 'For information only',
      deviation: 'Deviation',
      deviationStricter: 'Deviation · stricter',
      deviationLooser: 'Deviation · looser',
      deviationMoreFavourable: 'Deviation · more favourable than the statute',
      deviationLessFavourable: 'Deviation · less favourable than the statute',
      deviationEqual: 'Deviation · same as the statute',
      deviationMixed: 'Deviation · partly stricter, partly looser',
      deviationMixedFavourable: 'Deviation · partly more, partly less favourable',
    },
    hint: {
      stricter: 'Stricter than the statute – allowed on any basis.',
      looser: 'Looser than the statute – this needs a collective agreement, a works agreement or a permit as its basis.',
      moreFavourable: 'More favourable than the statute – allowed on any basis.',
      lessFavourable: 'Less favourable than the statute – this needs a collective agreement, a works agreement or a permit as its basis.',
      mixed: 'Partly stricter, partly looser than the statute – this needs a collective agreement, a works agreement or a permit as its basis.',
      mixedFavourable: 'Partly more, partly less favourable than the statute – this needs a collective agreement, a works agreement or a permit as its basis.',
      equal: 'Same as the statutory value – no deviation is needed for this.',
      incomparable: 'This rule has no “stricter” or “looser” – any basis will do.',
      blocked: 'A company policy must not fall short of the statute. Choose a collective agreement, a works agreement or a permit as the basis – or a value that at least matches the statute.',
    },
    table: {
      rule: 'Rule',
      applies: 'Applies',
      note: 'Note on the legal position',
      deviate: 'Deviate',
      backToStatute: 'Back to the statute',
      statutoryWouldBe: 'By statute it would be: {value}',
      statutoryWouldBeNone: 'The statute sets no rule here.',
      basis: 'Basis: {name}',
      basisWithClause: 'Basis: {name}, {clause}',
      coveredDeviation: 'With collective-agreement code {code}: {value} ({name})',
      futureDeviation: 'From {date}: {value} ({name})',
      fallbackApplies: 'The statute sets no window here – this one applies until an agreement sets another.',
      fallbackWouldBe: 'The statute sets no window here – without the deviation it would be: {value}',
      editDeviationOf: 'Edit the deviation of “{name}”',
      backToStatuteOf: 'Take the deviation of “{name}” back to the statute',
    },
    notice: {
      fallback: 'The catalogue of statutory rules is temporarily unavailable. The built-in baseline is shown; recent changes may be missing.',
      unjudged: 'People no statutory rule currently applies to: {count}. They have no work location and no default jurisdiction is set – choose one below.',
      upcoming: 'From {date} the statutory rule “{rule}” changes to: {value}.',
      upcomingNone: 'From {date} the statutory rule “{rule}” no longer applies.',
      upcomingDeviationStays: 'Your deviation stays in place.',
      reviewTitle: 'Please review: {rule}',
      review: 'The statutory value has changed since this deviation was set ({agreement}). The statute now says: {statutory}. Your deviation: {deviation}.',
      keepDeviation: 'Keep deviation',
    },
    minors: {
      title: 'Young people under 18',
      headcount: 'People: {count}',
      headcountNone: 'none at the moment',
      hint: 'For people under 18 these statutory youth-protection rules apply instead of the general ones – automatically, from the date of birth kept in People. Agreements do not deviate from them.',
      generalApplies: 'No separate rule yet – the general one applies until then.',
      upcoming: 'From {date}: {value}',
      upcomingNone: 'From {date} this youth-protection rule no longer applies.',
    },
    form: {
      value: 'Deviating value',
      daysUnit: 'days',
      stageAfterHours: 'Working time of more than (h)',
      stageMinutes: 'Total break (min)',
      tierAfterHours: 'From overtime hours per week (h)',
      tierPercent: 'Premium (%)',
      addStep: 'Add step',
      removeStep: 'Remove step {row}',
      stageAfterHoursRow: 'Step {row}: working time of more than (h)',
      stageMinutesRow: 'Step {row}: total break (min)',
      tierAfterHoursRow: 'Step {row}: from overtime hours per week (h)',
      tierPercentRow: 'Step {row}: premium (%)',
      averageHours: 'Hours per week on average',
      averageWeeks: 'Reference period (weeks)',
      windowStart: 'Start',
      windowEnd: 'End',
      windowHint: 'An end before the start means the window runs past midnight.',
      statutoryIs: 'The statute says: {value}',
      comparisonBase: 'Without a statutory rule, {value} applies – the deviation is measured against it.',
      basis: 'Basis',
      basisHint: 'The agreement the deviation rests on.',
      basisPick: 'Choose a basis…',
      basisNew: 'Create a new basis…',
      basisRequired: 'Please choose a basis.',
      clause: 'Clause',
      clauseHint: 'Where the provision is found, e.g. “section 4(2)”. Optional.',
      invalid: {
        hours: 'Enter hours: more than 0 and at most 168.',
        days: 'Enter days: more than 0 and at most 366.',
        breakLadder: 'Check the steps: 1 to 6 steps, ascending by working time (more than 0 up to 24 h), a break of more than 0 up to 480 min and never less than in the step before.',
        weeklyAverage: 'Check the values: hours more than 0 and at most 168, reference period 1 to 104 weeks.',
        timeWindow: 'Enter a start and an end time; they must differ.',
        percentTiers: 'Check the steps: 1 to 6 steps, the first from 0 overtime hours, the others ascending; premium between 0 and 300 % with at most two decimals.',
      },
    },
    agreement: {
      name: 'Name',
      title: 'Agreements',
      subtitle: 'Collective agreements, works agreements, permits and company policies – the bases of your deviations.',
      new: 'New agreement',
      edit: 'Edit agreement',
      empty: 'No agreements yet – the statutory rules apply throughout.',
      inactive: 'Not in force at present',
      migrated: 'Carried over from earlier settings',
      noDeviations: 'No deviations yet. Set them above, on the rule they belong to.',
      needsReview: 'Needs review',
      kind: 'Kind of agreement',
      companyPolicyHint: 'A company policy may only be stricter or more favourable than the statute.',
      nameHint: 'For example “Metal industry framework agreement” or “Works agreement on breaks 2026”.',
      nameRequired: 'Please give the agreement a name.',
      reference: 'Reference number',
      referenceHint: 'Optional, e.g. the register number or the permit number.',
      jurisdiction: 'Jurisdiction',
      validFrom: 'Valid from',
      validFromRequired: 'Please say from when the agreement applies.',
      validTo: 'Valid until',
      validToHint: 'Leave empty if the agreement has no end date.',
      validSince: 'valid from {from}',
      validRange: 'valid {from} – {to}',
      scope: 'Applies to',
      scopeAll: 'everyone in this jurisdiction',
      scopeCovered: 'only people bound by the collective agreement',
      scopeCoveredCode: 'only collective-agreement code {code}',
      coverageCode: 'Collective-agreement code',
      coverageCodeHint: 'The code is set on a person’s employment contract in People. The agreement applies only to people with exactly this code.',
      deleteTitle: 'Delete “{name}”?',
      deleteConfirm: 'Deleting the agreement also deletes its deviations (number: {count}). Those rules follow the statute again afterwards.',
      deleteConfirmEmpty: 'The agreement carries no deviations; the rules in force do not change.',
      editDeviation: 'Edit deviation: {rule}',
      removeDeviation: 'Back to the statute: {rule}',
    },
    backConfirm: {
      title: '“{rule}”: back to the statute?',
      body: 'The deviation is removed. The statutory value applies again afterwards: {value}.',
      bodyNone: 'The deviation is removed. The statute sets no rule here – afterwards none applies.',
      bodyFallback: 'The deviation is removed. The statute sets no rule here – afterwards {value} applies again.',
    },
    workspace: {
      title: 'Workspace settings',
      defaultJurisdiction: 'Default jurisdiction',
      defaultJurisdictionHint: 'Applies to people without a work location, or with one the catalogue does not cover.',
      noDefault: 'No default',
      mode: 'When a booking breaks a rule',
      modeFlexible: 'Warn and record',
      modeFlexibleHint: 'The booking is saved. The violation is shown and recorded.',
      modeStrict: 'Block the booking',
      modeStrictHint: 'A booking that breaks an enforced rule cannot be saved.',
      originSignup: 'Taken from the country your company gave at signup: {country}.',
      originFallbackPrevious: 'Until the country your company gave at signup is known and in the catalogue, the rules of {current} apply – the regime this workspace was judged by before.',
      originFallbackUnknown: 'Your company’s country is not known yet, so the EU minimum applies until someone picks the country here.',
      originFallbackUncovered: 'At signup your company gave {country}. The catalogue has no rules for it yet, so the EU minimum applies.',
      originFallbackCovered: 'At signup your company gave {country}, whose rules are now in the catalogue. Pick it here to apply them – until then the EU minimum applies.',
    },
    toast: {
      settingsSaved: 'Settings saved',
      deviationSaved: 'Deviation saved',
      deviationConfirmed: 'Deviation confirmed',
      backToStatute: 'Deviation removed – the statute applies again',
      agreementSaved: 'Agreement saved',
      agreementDeleted: 'Agreement deleted',
      modelsCreated: 'Work-time models added: {count}',
      modelsExist: 'This country’s work-time models already exist.',
    },
    errors: {
      rival: '“{name}” already sets this rule for the same people in an overlapping period.',
      validToBeforeFrom: 'The end of validity must not be before its start.',
      coverageCodeRequired: 'Please enter the collective-agreement code the agreement applies to.',
      invalidValue: 'The value is not valid for this rule. Please check it.',
      jurisdictionUnknown: 'This jurisdiction is not part of the catalogue of statutory rules.',
      dateInvalid: 'Please enter a valid date.',
      labelInvalid: 'Check the text fields: the name is required and no field may be longer than 160 characters.',
      noModelPresets: 'There are no prepared work-time models for this country.',
      kindInvalid: 'Choose the kind of agreement from the list.',
      modeInvalid: 'Choose what happens when a booking breaks a rule.',
      ruleUnknown: 'This version of the app does not know this rule. Please reload the page.',
      ruleInfoOnly: 'This rule is shown for information only – there is nothing to deviate from.',
    },
    // The workspace page around the section (Administration › Statutory rules):
    // a workspace without Time, and a viewer time-api does not admit.
    page: {
      timeInactiveTitle: 'Time is not active in this workspace',
      timeInactive: 'The statutory rules apply to the working time recorded in Time. Once Time is part of this workspace, this is where you record where your company deviates from them.',
      noAccess: 'You cannot manage the statutory rules of this workspace. The workspace owner, the suite admins and Time members allowed to manage compliance can.',
    },
  },
  // Administration › Training occupations (ADR 0092): which of the recognised
  // training occupations in the operators' catalogue this workspace offers.
  // Titles, branches and regulations are German-only data, never translated.
  trainingOccupations: {
    title: 'Training occupations',
    columns: {
      occupation: 'Training occupation',
      duration: 'Duration',
      regulation: 'Training regulation',
      apprentices: 'Apprentices',
    },
    duration: '{count} months',
    regulationIssued: 'issued {date}',
    regulationValidFrom: 'in force since {date}',
    // The area a recognised training occupation belongs to: in Germany, the
    // chamber (or authority) that supervises it.
    areas: {
      IH: 'Industry and commerce (IHK)',
      Hw: 'Skilled crafts (HWK)',
      oeD: 'Public service',
      Lw: 'Agriculture',
      FB: 'Liberal professions',
      Hausw: 'Home economics',
      See: 'Maritime shipping',
    },
    // The two structure groups a catalogue row names (Fachrichtungen / Schwerpunkte).
    branches: {
      specialisations: 'Specialisations',
      focuses: 'Focus areas',
      // An offered occupation names its branches as one flat list, without saying which of the two they are.
      any: 'Specialisations / focus areas',
    },
    // The positions of the regulation (Ausbildungsberufsbild) on the trainees' skill
    // walls, per version of the regulation. `onWall` and (pl) `versions` are worded
    // count-neutrally on purpose: no plural form to get wrong.
    requirements: {
      onWall: 'Positions of the training regulation on the skill wall: {count}',
      version: 'regulation issued {date}',
      none: 'The positions of the training regulation are not available yet. They appear on the skill wall once the platform operator has imported them.',
      versions: 'Apprentices are trained under {count} versions of the regulation – each stays under the version in force when their training began.',
    },
    // An offered occupation the catalogue no longer simply offers, and what that means here.
    state: {
      notSelectable: 'No longer available for selection',
      notSelectableHint: 'The platform operator no longer offers this occupation for new selection. Your workspace keeps it and its apprentices – but once you remove it, it cannot be added again.',
      withdrawn: 'Recognition withdrawn',
      withdrawnHint: 'The state recognition of this occupation was repealed, or it is no longer in the directory of recognised training occupations. Apprentices already in training finish it; it cannot be added again.',
      unknown: 'Not in the catalogue',
      unknownHint: 'This entry was typed in before the catalogue existed and matches none of its occupations. It can only be removed – add the matching occupation from the catalogue instead.',
    },
    offered: {
      title: 'What we offer',
      intro: 'The recognised training occupations your company trains in. Only these can be chosen for an apprentice.',
      emptyTitle: 'No training occupation offered yet',
      empty: 'Choose the occupations your company trains in from the catalogue below. They can then be assigned to your apprentices.',
      browse: 'Choose from the catalogue',
      remove: 'Remove',
      removeNamed: 'Remove {title} from the offer',
    },
    catalogue: {
      title: 'Add from the catalogue',
      intro: 'Germany’s recognised training occupations that your workspace does not offer yet. The catalogue is kept up to date centrally.',
      unavailableTitle: 'The catalogue cannot be reached right now',
      unavailable: 'Your offer above is shown from what this workspace has stored. Occupations can be added again as soon as the catalogue is back.',
      allOffered: 'Your workspace already offers every occupation in the catalogue.',
      searchPlaceholder: 'Title or specialisation',
      areaLabel: 'Area',
      areaAll: 'All areas',
      count: 'Occupations found: {shown} of {total}',
      noMatches: 'No occupation matches your search.',
      resetFilters: 'Reset search',
      showMore: 'Show more',
      add: 'Add',
      addNamed: 'Add {title} to the offer',
    },
    removeConfirm: {
      title: 'Remove “{title}” from the offer?',
      apprentices: 'Apprentices currently assigned to this occupation: {count}. They keep their occupation and finish their training – it just can no longer be chosen for new apprentices.',
      final: 'Once removed, this occupation cannot be added again.',
      confirm: 'Remove from the offer',
    },
    toast: {
      added: '“{title}” is now part of your offer',
      removed: '“{title}” is no longer offered',
    },
    errors: {
      notOfferable: '“{title}” could not be added: the occupation has been switched off or withdrawn in the meantime. The list has been refreshed.',
      catalogueUnavailable: 'The catalogue cannot be reached right now, so nothing can be added. Please try again later.',
    },
    // The workspace page around the section (Administration › Training
    // occupations): a workspace without the apprenticeship module, and a viewer
    // apprentice-api does not admit.
    page: {
      moduleInactiveTitle: 'Apprenticeship is not active in this workspace',
      moduleInactive: 'Training occupations are offered for the apprentices managed in the Apprenticeship module. Once it is part of this workspace, this is where you choose which occupations your company trains in.',
      noAccess: 'You cannot manage the training occupations of this workspace. The workspace owner, the suite admins and members of the Apprenticeship module allowed to manage it can.',
    },
  },
};
